/**
 * Chroma subsampling format
 * @enum {0 | 1 | 2 | 3}
 */
export const ChromaSampling = Object.freeze({
    /**
     * Both vertically and horizontally subsampled.
     */
    Cs420: 0, "0": "Cs420",
    /**
     * Horizontally subsampled.
     */
    Cs422: 1, "1": "Cs422",
    /**
     * Not subsampled.
     */
    Cs444: 2, "2": "Cs444",
    /**
     * Monochrome.
     */
    Cs400: 3, "3": "Cs400",
});

export class IntoUnderlyingByteSource {
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        IntoUnderlyingByteSourceFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_intounderlyingbytesource_free(ptr, 0);
    }
    /**
     * @returns {number}
     */
    get autoAllocateChunkSize() {
        const ret = wasm.intounderlyingbytesource_autoAllocateChunkSize(this.__wbg_ptr);
        return ret >>> 0;
    }
    cancel() {
        const ptr = this.__destroy_into_raw();
        wasm.intounderlyingbytesource_cancel(ptr);
    }
    /**
     * @param {ReadableByteStreamController} controller
     * @returns {Promise<any>}
     */
    pull(controller) {
        const ret = wasm.intounderlyingbytesource_pull(this.__wbg_ptr, controller);
        return ret;
    }
    /**
     * @param {ReadableByteStreamController} controller
     */
    start(controller) {
        wasm.intounderlyingbytesource_start(this.__wbg_ptr, controller);
    }
    /**
     * @returns {ReadableStreamType}
     */
    get type() {
        const ret = wasm.intounderlyingbytesource_type(this.__wbg_ptr);
        return __wbindgen_enum_ReadableStreamType[ret];
    }
}
if (Symbol.dispose) IntoUnderlyingByteSource.prototype[Symbol.dispose] = IntoUnderlyingByteSource.prototype.free;

export class IntoUnderlyingSink {
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        IntoUnderlyingSinkFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_intounderlyingsink_free(ptr, 0);
    }
    /**
     * @param {any} reason
     * @returns {Promise<any>}
     */
    abort(reason) {
        const ptr = this.__destroy_into_raw();
        const ret = wasm.intounderlyingsink_abort(ptr, reason);
        return ret;
    }
    /**
     * @returns {Promise<any>}
     */
    close() {
        const ptr = this.__destroy_into_raw();
        const ret = wasm.intounderlyingsink_close(ptr);
        return ret;
    }
    /**
     * @param {any} chunk
     * @returns {Promise<any>}
     */
    write(chunk) {
        const ret = wasm.intounderlyingsink_write(this.__wbg_ptr, chunk);
        return ret;
    }
}
if (Symbol.dispose) IntoUnderlyingSink.prototype[Symbol.dispose] = IntoUnderlyingSink.prototype.free;

export class IntoUnderlyingSource {
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        IntoUnderlyingSourceFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_intounderlyingsource_free(ptr, 0);
    }
    cancel() {
        const ptr = this.__destroy_into_raw();
        wasm.intounderlyingsource_cancel(ptr);
    }
    /**
     * @param {ReadableStreamDefaultController} controller
     * @returns {Promise<any>}
     */
    pull(controller) {
        const ret = wasm.intounderlyingsource_pull(this.__wbg_ptr, controller);
        return ret;
    }
}
if (Symbol.dispose) IntoUnderlyingSource.prototype[Symbol.dispose] = IntoUnderlyingSource.prototype.free;

/**
 * A machine-readable representation of the authenticity for a `ShieldState`.
 * @enum {0 | 1 | 2 | 3 | 4 | 5}
 */
export const ShieldStateCode = Object.freeze({
    /**
     * Not enough information available to check the authenticity.
     */
    AuthenticityNotGuaranteed: 0, "0": "AuthenticityNotGuaranteed",
    /**
     * The sending device isn't yet known by the Client.
     */
    UnknownDevice: 1, "1": "UnknownDevice",
    /**
     * The sending device hasn't been verified by the sender.
     */
    UnsignedDevice: 2, "2": "UnsignedDevice",
    /**
     * The sender hasn't been verified by the Client's user.
     */
    UnverifiedIdentity: 3, "3": "UnverifiedIdentity",
    /**
     * The sender was previously verified but changed their identity.
     */
    VerificationViolation: 4, "4": "VerificationViolation",
    /**
     * The `sender` field on the event does not match the owner of the device
     * that established the Megolm session.
     */
    MismatchedSender: 5, "5": "MismatchedSender",
});

export class WasmClient {
    static __wrap(ptr) {
        const obj = Object.create(WasmClient.prototype);
        obj.__wbg_ptr = ptr;
        WasmClientFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        WasmClientFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_wasmclient_free(ptr, 0);
    }
    /**
     * @param {string} flow_id
     * @param {string} other_user_id
     * @param {Function} on_event
     * @returns {Promise<boolean>}
     */
    acceptAndObserveVerification(flow_id, other_user_id, on_event) {
        const ptr0 = passStringToWasm0(flow_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(other_user_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_acceptAndObserveVerification(this.__wbg_ptr, ptr0, len0, ptr1, len1, on_event);
        return ret;
    }
    /**
     * @param {string} room_id
     * @returns {Promise<any>}
     */
    acceptInvite(room_id) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_acceptInvite(this.__wbg_ptr, ptr0, len0);
        return ret;
    }
    /**
     * @param {string} room_id
     * @param {string} user_id
     * @returns {Promise<any>}
     */
    acceptKnockRequest(room_id, user_id) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(user_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_acceptKnockRequest(this.__wbg_ptr, ptr0, len0, ptr1, len1);
        return ret;
    }
    /**
     * @param {string} flow_id
     * @param {string} other_user_id
     * @returns {Promise<boolean>}
     */
    acceptSas(flow_id, other_user_id) {
        const ptr0 = passStringToWasm0(flow_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(other_user_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_acceptSas(this.__wbg_ptr, ptr0, len0, ptr1, len1);
        return ret;
    }
    /**
     * @param {string} flow_id
     * @param {string} other_user_id
     * @returns {Promise<boolean>}
     */
    acceptVerificationRequest(flow_id, other_user_id) {
        const ptr0 = passStringToWasm0(flow_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(other_user_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_acceptVerificationRequest(this.__wbg_ptr, ptr0, len0, ptr1, len1);
        return ret;
    }
    /**
     * @param {string} event_type
     * @returns {Promise<any>}
     */
    accountData(event_type) {
        const ptr0 = passStringToWasm0(event_type, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_accountData(this.__wbg_ptr, ptr0, len0);
        return ret;
    }
    /**
     * @returns {Promise<any>}
     */
    accountManagementUrl() {
        const ret = wasm.wasmclient_accountManagementUrl(this.__wbg_ptr);
        return ret;
    }
    /**
     * @param {string} keyword
     * @returns {Promise<any>}
     */
    addNotificationKeyword(keyword) {
        const ptr0 = passStringToWasm0(keyword, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_addNotificationKeyword(this.__wbg_ptr, ptr0, len0);
        return ret;
    }
    /**
     * @param {string} room_id
     * @returns {Promise<boolean>}
     */
    allUsersVerified(room_id) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_allUsersVerified(this.__wbg_ptr, ptr0, len0);
        return ret;
    }
    /**
     * @param {string} room_id
     * @param {string} changes_json
     * @returns {Promise<boolean>}
     */
    applyPowerLevelChanges(room_id, changes_json) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(changes_json, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_applyPowerLevelChanges(this.__wbg_ptr, ptr0, len0, ptr1, len1);
        return ret;
    }
    /**
     * @param {string} presence
     * @returns {Promise<any>}
     */
    applySyncPresence(presence) {
        const ptr0 = passStringToWasm0(presence, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_applySyncPresence(this.__wbg_ptr, ptr0, len0);
        return ret;
    }
    /**
     * @param {boolean} fetch
     * @returns {Promise<boolean>}
     */
    backupExistsOnServer(fetch) {
        const ret = wasm.wasmclient_backupExistsOnServer(this.__wbg_ptr, fetch);
        return ret;
    }
    /**
     * @param {string} room_id
     * @param {string} user_id
     * @param {string | null} [reason]
     * @returns {Promise<any>}
     */
    banUser(room_id, user_id, reason) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(user_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        var ptr2 = isLikeNone(reason) ? 0 : passStringToWasm0(reason, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        var len2 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_banUser(this.__wbg_ptr, ptr0, len0, ptr1, len1, ptr2, len2);
        return ret;
    }
    /**
     * @param {number} session_id
     * @param {string} message
     * @returns {boolean}
     */
    callWidgetFromWebview(session_id, message) {
        const ptr0 = passStringToWasm0(message, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_callWidgetFromWebview(this.__wbg_ptr, session_id, ptr0, len0);
        return ret !== 0;
    }
    /**
     * @param {string} room_id
     * @returns {Promise<any>}
     */
    canEditImagePacks(room_id) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_canEditImagePacks(this.__wbg_ptr, ptr0, len0);
        return ret;
    }
    /**
     * @returns {Promise<any>}
     */
    canSetProfileFields() {
        const ret = wasm.wasmclient_canSetProfileFields(this.__wbg_ptr);
        return ret;
    }
    /**
     * @param {string} room_id
     * @param {string} user_id
     * @returns {Promise<any>}
     */
    canUserBan(room_id, user_id) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(user_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_canUserBan(this.__wbg_ptr, ptr0, len0, ptr1, len1);
        return ret;
    }
    /**
     * @param {string} room_id
     * @param {string} user_id
     * @returns {Promise<any>}
     */
    canUserInvite(room_id, user_id) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(user_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_canUserInvite(this.__wbg_ptr, ptr0, len0, ptr1, len1);
        return ret;
    }
    /**
     * @param {string} room_id
     * @param {string} user_id
     * @returns {Promise<any>}
     */
    canUserRedactOther(room_id, user_id) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(user_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_canUserRedactOther(this.__wbg_ptr, ptr0, len0, ptr1, len1);
        return ret;
    }
    /**
     * @param {string} room_id
     * @param {string} txn_id
     * @returns {Promise<any>}
     */
    cancelByTxn(room_id, txn_id) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(txn_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_cancelByTxn(this.__wbg_ptr, ptr0, len0, ptr1, len1);
        return ret;
    }
    /**
     * @param {string} flow_id
     * @param {string} other_user_id
     * @returns {Promise<boolean>}
     */
    cancelVerificationWithUser(flow_id, other_user_id) {
        const ptr0 = passStringToWasm0(flow_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(other_user_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_cancelVerificationWithUser(this.__wbg_ptr, ptr0, len0, ptr1, len1);
        return ret;
    }
    /**
     * @param {string} flow_id
     * @returns {Promise<boolean>}
     */
    cancelVerification(flow_id) {
        const ptr0 = passStringToWasm0(flow_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_cancelVerification(this.__wbg_ptr, ptr0, len0);
        return ret;
    }
    /**
     * @param {number} days
     * @returns {Promise<any>}
     */
    clearMediaCacheOlderThan(days) {
        const ret = wasm.wasmclient_clearMediaCacheOlderThan(this.__wbg_ptr, days);
        return ret;
    }
    /**
     * @returns {Promise<any>}
     */
    clearMediaCache() {
        const ret = wasm.wasmclient_clearMediaCache(this.__wbg_ptr);
        return ret;
    }
    /**
     * @param {string} room_id
     * @returns {Promise<any>}
     */
    clearRoomMedia(room_id) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_clearRoomMedia(this.__wbg_ptr, ptr0, len0);
        return ret;
    }
    /**
     * @param {string} flow_id
     * @param {string} other_user_id
     * @returns {Promise<boolean>}
     */
    confirmSasWithUser(flow_id, other_user_id) {
        const ptr0 = passStringToWasm0(flow_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(other_user_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_confirmSasWithUser(this.__wbg_ptr, ptr0, len0, ptr1, len1);
        return ret;
    }
    /**
     * @param {string} flow_id
     * @returns {Promise<boolean>}
     */
    confirmSas(flow_id) {
        const ptr0 = passStringToWasm0(flow_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_confirmSas(this.__wbg_ptr, ptr0, len0);
        return ret;
    }
    /**
     * @param {string} homeserver_url
     * @param {string} _base_store_dir
     * @param {string | null} [account_id]
     * @param {string | null} [_proxy]
     * @param {boolean | null} [enable_share_history_on_invite]
     * @returns {Promise<WasmClient>}
     */
    static createAsync(homeserver_url, _base_store_dir, account_id, _proxy, enable_share_history_on_invite) {
        const ptr0 = passStringToWasm0(homeserver_url, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(_base_store_dir, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        var ptr2 = isLikeNone(account_id) ? 0 : passStringToWasm0(account_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        var len2 = WASM_VECTOR_LEN;
        var ptr3 = isLikeNone(_proxy) ? 0 : passStringToWasm0(_proxy, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        var len3 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_createAsync(ptr0, len0, ptr1, len1, ptr2, len2, ptr3, len3, isLikeNone(enable_share_history_on_invite) ? 0xFFFFFF : enable_share_history_on_invite ? 1 : 0);
        return ret;
    }
    /**
     * @param {string | null | undefined} name
     * @param {string | null | undefined} topic
     * @param {string[]} invitees
     * @param {boolean} is_public
     * @param {string | null} [room_alias]
     * @returns {Promise<any>}
     */
    createRoom(name, topic, invitees, is_public, room_alias) {
        var ptr0 = isLikeNone(name) ? 0 : passStringToWasm0(name, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        var len0 = WASM_VECTOR_LEN;
        var ptr1 = isLikeNone(topic) ? 0 : passStringToWasm0(topic, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        var len1 = WASM_VECTOR_LEN;
        const ptr2 = passArrayJsValueToWasm0(invitees, wasm.__wbindgen_malloc);
        const len2 = WASM_VECTOR_LEN;
        var ptr3 = isLikeNone(room_alias) ? 0 : passStringToWasm0(room_alias, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        var len3 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_createRoom(this.__wbg_ptr, ptr0, len0, ptr1, len1, ptr2, len2, is_public, ptr3, len3);
        return ret;
    }
    /**
     * @param {string} name
     * @param {string} space_id
     * @returns {Promise<any>}
     */
    createSection(name, space_id) {
        const ptr0 = passStringToWasm0(name, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(space_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_createSection(this.__wbg_ptr, ptr0, len0, ptr1, len1);
        return ret;
    }
    /**
     * @param {string} name
     * @param {string | null | undefined} topic
     * @param {boolean} is_public
     * @param {string[]} invitees
     * @returns {Promise<any>}
     */
    createSpace(name, topic, is_public, invitees) {
        const ptr0 = passStringToWasm0(name, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        var ptr1 = isLikeNone(topic) ? 0 : passStringToWasm0(topic, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        var len1 = WASM_VECTOR_LEN;
        const ptr2 = passArrayJsValueToWasm0(invitees, wasm.__wbindgen_malloc);
        const len2 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_createSpace(this.__wbg_ptr, ptr0, len0, ptr1, len1, is_public, ptr2, len2);
        return ret;
    }
    /**
     * @param {string} room_id
     * @param {string} notification_event_id
     * @returns {Promise<any>}
     */
    declineCall(room_id, notification_event_id) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(notification_event_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_declineCall(this.__wbg_ptr, ptr0, len0, ptr1, len1);
        return ret;
    }
    /**
     * @param {string} room_id
     * @param {string} user_id
     * @param {string | null} [reason]
     * @returns {Promise<any>}
     */
    declineKnockRequest(room_id, user_id, reason) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(user_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        var ptr2 = isLikeNone(reason) ? 0 : passStringToWasm0(reason, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        var len2 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_declineKnockRequest(this.__wbg_ptr, ptr0, len0, ptr1, len1, ptr2, len2);
        return ret;
    }
    /**
     * @param {string} name
     * @returns {Promise<any>}
     */
    deleteProfileField(name) {
        const ptr0 = passStringToWasm0(name, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_deleteProfileField(this.__wbg_ptr, ptr0, len0);
        return ret;
    }
    /**
     * @param {string} tag
     * @returns {Promise<any>}
     */
    deleteSection(tag) {
        const ptr0 = passStringToWasm0(tag, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_deleteSection(this.__wbg_ptr, ptr0, len0);
        return ret;
    }
    /**
     * @param {string} room_id
     * @returns {Promise<any>}
     */
    dmPeerUserId(room_id) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_dmPeerUserId(this.__wbg_ptr, ptr0, len0);
        return ret;
    }
    /**
     * @param {string} att_json
     * @param {string | null} [_filename_hint]
     * @returns {Promise<any>}
     */
    downloadAttachmentToCacheFile(att_json, _filename_hint) {
        const ptr0 = passStringToWasm0(att_json, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        var ptr1 = isLikeNone(_filename_hint) ? 0 : passStringToWasm0(_filename_hint, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        var len1 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_downloadAttachmentToCacheFile(this.__wbg_ptr, ptr0, len0, ptr1, len1);
        return ret;
    }
    /**
     * @param {string} room_id
     * @param {string} target_event_id
     * @param {string | null} [caption]
     * @param {string | null} [formatted_caption]
     * @returns {Promise<any>}
     */
    editCaption(room_id, target_event_id, caption, formatted_caption) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(target_event_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        var ptr2 = isLikeNone(caption) ? 0 : passStringToWasm0(caption, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        var len2 = WASM_VECTOR_LEN;
        var ptr3 = isLikeNone(formatted_caption) ? 0 : passStringToWasm0(formatted_caption, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        var len3 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_editCaption(this.__wbg_ptr, ptr0, len0, ptr1, len1, ptr2, len2, ptr3, len3);
        return ret;
    }
    /**
     * @param {string} room_id
     * @param {string} poll_event_id
     * @param {string} question
     * @param {string[]} answers
     * @param {string} kind
     * @param {number} max_selections
     * @returns {Promise<boolean>}
     */
    editPoll(room_id, poll_event_id, question, answers, kind, max_selections) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(poll_event_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        const ptr2 = passStringToWasm0(question, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len2 = WASM_VECTOR_LEN;
        const ptr3 = passArrayJsValueToWasm0(answers, wasm.__wbindgen_malloc);
        const len3 = WASM_VECTOR_LEN;
        const ptr4 = passStringToWasm0(kind, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len4 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_editPoll(this.__wbg_ptr, ptr0, len0, ptr1, len1, ptr2, len2, ptr3, len3, ptr4, len4, max_selections);
        return ret;
    }
    /**
     * @param {string} room_id
     * @param {string} target_event_id
     * @param {string} new_body
     * @param {string | null} [formatted_body]
     * @returns {Promise<any>}
     */
    edit(room_id, target_event_id, new_body, formatted_body) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(target_event_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        const ptr2 = passStringToWasm0(new_body, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len2 = WASM_VECTOR_LEN;
        var ptr3 = isLikeNone(formatted_body) ? 0 : passStringToWasm0(formatted_body, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        var len3 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_edit(this.__wbg_ptr, ptr0, len0, ptr1, len1, ptr2, len2, ptr3, len3);
        return ret;
    }
    /**
     * @param {string} room_id
     * @returns {Promise<any>}
     */
    enableRoomEncryption(room_id) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_enableRoomEncryption(this.__wbg_ptr, ptr0, len0);
        return ret;
    }
    /**
     * @param {string} room_id
     * @param {string} user_id
     * @returns {Promise<any>}
     */
    ensureDmIfAllowed(room_id, user_id) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(user_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_ensureDmIfAllowed(this.__wbg_ptr, ptr0, len0, ptr1, len1);
        return ret;
    }
    /**
     * @param {string} user_id
     * @returns {Promise<any>}
     */
    ensureDm(user_id) {
        const ptr0 = passStringToWasm0(user_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_ensureDm(this.__wbg_ptr, ptr0, len0);
        return ret;
    }
    enterBackground() {
        wasm.wasmclient_enterBackground(this.__wbg_ptr);
    }
    enterForeground() {
        wasm.wasmclient_enterForeground(this.__wbg_ptr);
    }
    /**
     * @param {string} room_id
     * @param {number} ts_ms
     * @param {boolean} backward
     * @returns {Promise<any>}
     */
    eventAtTimestamp(room_id, ts_ms, backward) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_eventAtTimestamp(this.__wbg_ptr, ptr0, len0, ts_ms, backward);
        return ret;
    }
    /**
     * @param {string} room_id
     * @param {string} event_id
     * @returns {Promise<any>}
     */
    eventDetails(room_id, event_id) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(event_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_eventDetails(this.__wbg_ptr, ptr0, len0, ptr1, len1);
        return ret;
    }
    /**
     * @param {string} room_id
     * @param {string} event_id
     * @returns {Promise<any>}
     */
    fetchNotification(room_id, event_id) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(event_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_fetchNotification(this.__wbg_ptr, ptr0, len0, ptr1, len1);
        return ret;
    }
    /**
     * @param {number} since_ts_ms
     * @param {number} max_rooms
     * @param {number} max_events
     * @returns {Promise<any>}
     */
    fetchNotificationsSince(since_ts_ms, max_rooms, max_events) {
        const ret = wasm.wasmclient_fetchNotificationsSince(this.__wbg_ptr, since_ts_ms, max_rooms, max_events);
        return ret;
    }
    /**
     * @param {string} callback_url_or_query
     * @param {string} _expected_state
     * @param {string | null} [_expected_issuer]
     * @returns {Promise<any>}
     */
    finishLoginFromRedirect(callback_url_or_query, _expected_state, _expected_issuer) {
        const ptr0 = passStringToWasm0(callback_url_or_query, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(_expected_state, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        var ptr2 = isLikeNone(_expected_issuer) ? 0 : passStringToWasm0(_expected_issuer, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        var len2 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_finishLoginFromRedirect(this.__wbg_ptr, ptr0, len0, ptr1, len1, ptr2, len2);
        return ret;
    }
    /**
     * @param {string} source_room_id
     * @param {string} event_id
     * @param {string[]} target_room_ids
     * @returns {Promise<any>}
     */
    forwardEvent(source_room_id, event_id, target_room_ids) {
        const ptr0 = passStringToWasm0(source_room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(event_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        const ptr2 = passArrayJsValueToWasm0(target_room_ids, wasm.__wbindgen_malloc);
        const len2 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_forwardEvent(this.__wbg_ptr, ptr0, len0, ptr1, len1, ptr2, len2);
        return ret;
    }
    /**
     * @param {boolean} is_encrypted
     * @param {boolean} is_one_to_one
     * @returns {Promise<any>}
     */
    getDefaultRoomNotificationMode(is_encrypted, is_one_to_one) {
        const ret = wasm.wasmclient_getDefaultRoomNotificationMode(this.__wbg_ptr, is_encrypted, is_one_to_one);
        return ret;
    }
    /**
     * @param {string} url
     * @returns {Promise<any>}
     */
    getLinkPreview(url) {
        const ptr0 = passStringToWasm0(url, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_getLinkPreview(this.__wbg_ptr, ptr0, len0);
        return ret;
    }
    /**
     * @param {string} info_json
     * @param {boolean} _use_thumbnail
     * @returns {Promise<any>}
     */
    getMediaContent(info_json, _use_thumbnail) {
        const ptr0 = passStringToWasm0(info_json, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_getMediaContent(this.__wbg_ptr, ptr0, len0, _use_thumbnail);
        return ret;
    }
    /**
     * @returns {Promise<any>}
     */
    getNotificationKeywords() {
        const ret = wasm.wasmclient_getNotificationKeywords(this.__wbg_ptr);
        return ret;
    }
    /**
     * @param {string} room_id
     * @returns {Promise<any>}
     */
    getPinnedEvents(room_id) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_getPinnedEvents(this.__wbg_ptr, ptr0, len0);
        return ret;
    }
    /**
     * @param {string} user_id
     * @returns {Promise<any>}
     */
    getPresence(user_id) {
        const ptr0 = passStringToWasm0(user_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_getPresence(this.__wbg_ptr, ptr0, len0);
        return ret;
    }
    /**
     * @param {string} room_id
     * @param {string} user_id
     * @returns {Promise<number>}
     */
    getUserPowerLevel(room_id, user_id) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(user_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_getUserPowerLevel(this.__wbg_ptr, ptr0, len0, ptr1, len1);
        return ret;
    }
    /**
     * @param {string} user_id
     * @returns {Promise<any>}
     */
    getUserProfile(user_id) {
        const ptr0 = passStringToWasm0(user_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_getUserProfile(this.__wbg_ptr, ptr0, len0);
        return ret;
    }
    /**
     * @returns {Promise<any>}
     */
    homeserverLoginDetails() {
        const ret = wasm.wasmclient_homeserverLoginDetails(this.__wbg_ptr);
        return ret;
    }
    /**
     * @returns {string}
     */
    homeserverUrl() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.wasmclient_homeserverUrl(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * @param {string} user_id
     * @returns {Promise<any>}
     */
    ignoreUser(user_id) {
        const ptr0 = passStringToWasm0(user_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_ignoreUser(this.__wbg_ptr, ptr0, len0);
        return ret;
    }
    /**
     * @returns {Promise<any>}
     */
    ignoredUsers() {
        const ret = wasm.wasmclient_ignoredUsers(this.__wbg_ptr);
        return ret;
    }
    /**
     * @returns {Promise<any>}
     */
    inviteBlocked() {
        const ret = wasm.wasmclient_inviteBlocked(this.__wbg_ptr);
        return ret;
    }
    /**
     * @param {string} room_id
     * @param {string} user_id
     * @returns {Promise<any>}
     */
    inviteUser(room_id, user_id) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(user_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_inviteUser(this.__wbg_ptr, ptr0, len0, ptr1, len1);
        return ret;
    }
    /**
     * @param {string} room_id
     * @param {string} event_id
     * @param {string} user_id
     * @returns {Promise<any>}
     */
    isEventReadBy(room_id, event_id, user_id) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(event_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        const ptr2 = passStringToWasm0(user_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len2 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_isEventReadBy(this.__wbg_ptr, ptr0, len0, ptr1, len1, ptr2, len2);
        return ret;
    }
    /**
     * @returns {boolean}
     */
    isLoggedIn() {
        const ret = wasm.wasmclient_isLoggedIn(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * @param {string} kind
     * @param {string} rule_id
     * @returns {Promise<any>}
     */
    isPushRuleEnabled(kind, rule_id) {
        const ptr0 = passStringToWasm0(kind, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(rule_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_isPushRuleEnabled(this.__wbg_ptr, ptr0, len0, ptr1, len1);
        return ret;
    }
    /**
     * @returns {Promise<any>}
     */
    isReactionNotificationsEnabled() {
        const ret = wasm.wasmclient_isReactionNotificationsEnabled(this.__wbg_ptr);
        return ret;
    }
    /**
     * @param {string} room_id
     * @returns {Promise<any>}
     */
    isSpace(room_id) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_isSpace(this.__wbg_ptr, ptr0, len0);
        return ret;
    }
    /**
     * @param {string} user_id
     * @returns {Promise<any>}
     */
    isUserIgnored(user_id) {
        const ptr0 = passStringToWasm0(user_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_isUserIgnored(this.__wbg_ptr, ptr0, len0);
        return ret;
    }
    /**
     * @param {string} user_id
     * @returns {Promise<boolean>}
     */
    isUserVerified(user_id) {
        const ptr0 = passStringToWasm0(user_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_isUserVerified(this.__wbg_ptr, ptr0, len0);
        return ret;
    }
    /**
     * @param {string} id_or_alias
     * @param {string[] | null} [via]
     * @returns {Promise<void>}
     */
    joinByIdOrAlias(id_or_alias, via) {
        const ptr0 = passStringToWasm0(id_or_alias, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        var ptr1 = isLikeNone(via) ? 0 : passArrayJsValueToWasm0(via, wasm.__wbindgen_malloc);
        var len1 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_joinByIdOrAlias(this.__wbg_ptr, ptr0, len0, ptr1, len1);
        return ret;
    }
    /**
     * @param {string} room_id
     * @param {string} user_id
     * @param {string | null} [reason]
     * @returns {Promise<any>}
     */
    kickUser(room_id, user_id, reason) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(user_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        var ptr2 = isLikeNone(reason) ? 0 : passStringToWasm0(reason, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        var len2 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_kickUser(this.__wbg_ptr, ptr0, len0, ptr1, len1, ptr2, len2);
        return ret;
    }
    /**
     * @param {string} id_or_alias
     * @param {string[]} via
     * @returns {Promise<any>}
     */
    knock(id_or_alias, via) {
        const ptr0 = passStringToWasm0(id_or_alias, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArrayJsValueToWasm0(via, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_knock(this.__wbg_ptr, ptr0, len0, ptr1, len1);
        return ret;
    }
    /**
     * @param {string} room_id
     * @returns {Promise<any>}
     */
    leaveRoom(room_id) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_leaveRoom(this.__wbg_ptr, ptr0, len0);
        return ret;
    }
    /**
     * @param {boolean} refresh
     * @returns {Promise<any>}
     */
    listAllImagePacks(refresh) {
        const ret = wasm.wasmclient_listAllImagePacks(this.__wbg_ptr, refresh);
        return ret;
    }
    /**
     * @param {string} room_id
     * @returns {Promise<any>}
     */
    listBannedMembers(room_id) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_listBannedMembers(this.__wbg_ptr, ptr0, len0);
        return ret;
    }
    /**
     * @param {string} room_id
     * @returns {Promise<any>}
     */
    listImagePacks(room_id) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_listImagePacks(this.__wbg_ptr, ptr0, len0);
        return ret;
    }
    /**
     * @returns {Promise<any>}
     */
    listInvited() {
        const ret = wasm.wasmclient_listInvited(this.__wbg_ptr);
        return ret;
    }
    /**
     * @param {string} room_id
     * @returns {Promise<any>}
     */
    listKnockRequests(room_id) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_listKnockRequests(this.__wbg_ptr, ptr0, len0);
        return ret;
    }
    /**
     * @param {string} room_id
     * @returns {Promise<any>}
     */
    listMembers(room_id) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_listMembers(this.__wbg_ptr, ptr0, len0);
        return ret;
    }
    /**
     * @returns {Promise<any>}
     */
    listMyDevices() {
        const ret = wasm.wasmclient_listMyDevices(this.__wbg_ptr);
        return ret;
    }
    /**
     * @returns {Promise<any>}
     */
    listSections() {
        const ret = wasm.wasmclient_listSections(this.__wbg_ptr);
        return ret;
    }
    /**
     * @returns {any}
     */
    loadRoomListCache() {
        const ret = wasm.wasmclient_loadRoomListCache(this.__wbg_ptr);
        return ret;
    }
    /**
     * @param {string} username
     * @param {string} password
     * @param {string | null} [device_display_name]
     * @returns {Promise<string | undefined>}
     */
    loginAsync(username, password, device_display_name) {
        const ptr0 = passStringToWasm0(username, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(password, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        var ptr2 = isLikeNone(device_display_name) ? 0 : passStringToWasm0(device_display_name, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        var len2 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_loginAsync(this.__wbg_ptr, ptr0, len0, ptr1, len1, ptr2, len2);
        return ret;
    }
    /**
     * @param {string} email
     * @param {string} password
     * @param {string | null} [device_display_name]
     * @returns {Promise<string | undefined>}
     */
    loginEmail(email, password, device_display_name) {
        const ptr0 = passStringToWasm0(email, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(password, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        var ptr2 = isLikeNone(device_display_name) ? 0 : passStringToWasm0(device_display_name, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        var len2 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_loginEmail(this.__wbg_ptr, ptr0, len0, ptr1, len1, ptr2, len2);
        return ret;
    }
    /**
     * @param {string} redirect_uri
     * @param {string | null} [_device_name]
     * @returns {Promise<any>}
     */
    loginOauthBrowser(redirect_uri, _device_name) {
        const ptr0 = passStringToWasm0(redirect_uri, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        var ptr1 = isLikeNone(_device_name) ? 0 : passStringToWasm0(_device_name, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        var len1 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_loginOauthBrowser(this.__wbg_ptr, ptr0, len0, ptr1, len1);
        return ret;
    }
    /**
     * @param {string} country
     * @param {string} phone
     * @param {string} password
     * @param {string | null} [device_display_name]
     * @returns {Promise<string | undefined>}
     */
    loginPhone(country, phone, password, device_display_name) {
        const ptr0 = passStringToWasm0(country, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(phone, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        const ptr2 = passStringToWasm0(password, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len2 = WASM_VECTOR_LEN;
        var ptr3 = isLikeNone(device_display_name) ? 0 : passStringToWasm0(device_display_name, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        var len3 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_loginPhone(this.__wbg_ptr, ptr0, len0, ptr1, len1, ptr2, len2, ptr3, len3);
        return ret;
    }
    /**
     * @returns {Promise<any>}
     */
    logout() {
        const ret = wasm.wasmclient_logout(this.__wbg_ptr);
        return ret;
    }
    /**
     * @param {string} room_id
     * @param {string} event_id
     * @param {boolean} send_public_receipt
     * @returns {Promise<any>}
     */
    markFullyReadAt(room_id, event_id, send_public_receipt) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(event_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_markFullyReadAt(this.__wbg_ptr, ptr0, len0, ptr1, len1, send_public_receipt);
        return ret;
    }
    /**
     * @param {string} room_id
     * @param {string} event_id
     * @param {boolean} send_public_receipt
     * @returns {Promise<any>}
     */
    markReadAt(room_id, event_id, send_public_receipt) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(event_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_markReadAt(this.__wbg_ptr, ptr0, len0, ptr1, len1, send_public_receipt);
        return ret;
    }
    /**
     * @param {string} room_id
     * @param {boolean} send_public_receipt
     * @returns {Promise<any>}
     */
    markRead(room_id, send_public_receipt) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_markRead(this.__wbg_ptr, ptr0, len0, send_public_receipt);
        return ret;
    }
    /**
     * @param {string} room_id
     * @param {boolean} send_public_receipt
     * @returns {Promise<any>}
     */
    markRoomSeenLatest(room_id, send_public_receipt) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_markRoomSeenLatest(this.__wbg_ptr, ptr0, len0, send_public_receipt);
        return ret;
    }
    /**
     * @param {string} room_id
     * @param {string} thread_root_event_id
     * @param {string} event_id
     * @param {boolean} send_public_receipt
     * @returns {Promise<any>}
     */
    markThreadReadAt(room_id, thread_root_event_id, event_id, send_public_receipt) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(thread_root_event_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        const ptr2 = passStringToWasm0(event_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len2 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_markThreadReadAt(this.__wbg_ptr, ptr0, len0, ptr1, len1, ptr2, len2, send_public_receipt);
        return ret;
    }
    /**
     * @returns {Promise<any>}
     */
    mediaCacheOverview() {
        const ret = wasm.wasmclient_mediaCacheOverview(this.__wbg_ptr);
        return ret;
    }
    /**
     * @returns {Promise<any>}
     */
    mediaPreviewConfig() {
        const ret = wasm.wasmclient_mediaPreviewConfig(this.__wbg_ptr);
        return ret;
    }
    /**
     * @returns {Promise<any>}
     */
    mediaRetentionPolicy() {
        const ret = wasm.wasmclient_mediaRetentionPolicy(this.__wbg_ptr);
        return ret;
    }
    /**
     * @returns {Promise<any>}
     */
    mediaTypeUsage() {
        const ret = wasm.wasmclient_mediaTypeUsage(this.__wbg_ptr);
        return ret;
    }
    /**
     * @param {string} room_id
     * @param {string} user_id
     * @returns {Promise<any>}
     */
    memberActionState(room_id, user_id) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(user_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_memberActionState(this.__wbg_ptr, ptr0, len0, ptr1, len1);
        return ret;
    }
    /**
     * @param {string} room_id
     * @param {string} event_id
     * @param {string} sender_user_id
     * @returns {Promise<any>}
     */
    messageActionState(room_id, event_id, sender_user_id) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(event_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        const ptr2 = passStringToWasm0(sender_user_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len2 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_messageActionState(this.__wbg_ptr, ptr0, len0, ptr1, len1, ptr2, len2);
        return ret;
    }
    /**
     * @param {string} tag
     * @param {number} index
     * @returns {Promise<any>}
     */
    moveSection(tag, index) {
        const ptr0 = passStringToWasm0(tag, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_moveSection(this.__wbg_ptr, ptr0, len0, index);
        return ret;
    }
    /**
     * @param {string} user_id
     * @returns {Promise<any>}
     */
    mutualRooms(user_id) {
        const ptr0 = passStringToWasm0(user_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_mutualRooms(this.__wbg_ptr, ptr0, len0);
        return ret;
    }
    /**
     * @param {string} mxc_uri
     * @param {number} width
     * @param {number} height
     * @param {boolean} _crop
     * @returns {Promise<any>}
     */
    mxcThumbnailToCache(mxc_uri, width, height, _crop) {
        const ptr0 = passStringToWasm0(mxc_uri, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_mxcThumbnailToCache(this.__wbg_ptr, ptr0, len0, width, height, _crop);
        return ret;
    }
    /**
     * @returns {Promise<any>}
     */
    mySpaces() {
        const ret = wasm.wasmclient_mySpaces(this.__wbg_ptr);
        return ret;
    }
    /**
     * @param {Function} on_update
     * @returns {number}
     */
    observeBackupState(on_update) {
        const ret = wasm.wasmclient_observeBackupState(this.__wbg_ptr, on_update);
        return ret;
    }
    /**
     * @param {string} room_id
     * @param {string} notification_event_id
     * @param {Function} on_decline
     * @returns {number}
     */
    observeCallDecline(room_id, notification_event_id, on_decline) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(notification_event_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_observeCallDecline(this.__wbg_ptr, ptr0, len0, ptr1, len1, on_decline);
        return ret;
    }
    /**
     * @param {string} room_id
     * @param {Function} on_update
     * @returns {number}
     */
    observeLiveLocation(room_id, on_update) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_observeLiveLocation(this.__wbg_ptr, ptr0, len0, on_update);
        return ret;
    }
    /**
     * @param {string} room_id
     * @param {Function} on_changed
     * @returns {number}
     */
    observeOwnReceipt(room_id, on_changed) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_observeOwnReceipt(this.__wbg_ptr, ptr0, len0, on_changed);
        return ret;
    }
    /**
     * @param {string} room_id
     * @param {Function} on_changed
     * @returns {number}
     */
    observeReceipts(room_id, on_changed) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_observeReceipts(this.__wbg_ptr, ptr0, len0, on_changed);
        return ret;
    }
    /**
     * @param {Function} on_update
     * @returns {number}
     */
    observeRecoveryState(on_update) {
        const ret = wasm.wasmclient_observeRecoveryState(this.__wbg_ptr, on_update);
        return ret;
    }
    /**
     * @param {string} room_id
     * @param {Function} on_update
     * @returns {number}
     */
    observeRoomCallState(room_id, on_update) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_observeRoomCallState(this.__wbg_ptr, ptr0, len0, on_update);
        return ret;
    }
    /**
     * @param {string} room_id
     * @param {Function} on_update
     * @returns {number}
     */
    observeRoomInfo(room_id, on_update) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_observeRoomInfo(this.__wbg_ptr, ptr0, len0, on_update);
        return ret;
    }
    /**
     * @param {Function} on_reset
     * @param {Function} _on_update
     * @returns {number}
     */
    observeRoomList(on_reset, _on_update) {
        const ret = wasm.wasmclient_observeRoomList(this.__wbg_ptr, on_reset, _on_update);
        return ret;
    }
    /**
     * @param {Function} on_update
     * @returns {number}
     */
    observeSends(on_update) {
        const ret = wasm.wasmclient_observeSends(this.__wbg_ptr, on_update);
        return ret;
    }
    /**
     * @param {string} room_id
     * @param {Function} on_diff
     * @param {Function} on_error
     * @returns {number}
     */
    observeTimeline(room_id, on_diff, on_error) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_observeTimeline(this.__wbg_ptr, ptr0, len0, on_diff, on_error);
        return ret;
    }
    /**
     * @param {string} room_id
     * @param {Function} on_update
     * @returns {number}
     */
    observeTyping(room_id, on_update) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_observeTyping(this.__wbg_ptr, ptr0, len0, on_update);
        return ret;
    }
    /**
     * @param {string} room_id
     * @returns {Promise<any>}
     */
    ownLastRead(room_id) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_ownLastRead(this.__wbg_ptr, ptr0, len0);
        return ret;
    }
    /**
     * @returns {Promise<any>}
     */
    ownProfileFields() {
        const ret = wasm.wasmclient_ownProfileFields(this.__wbg_ptr);
        return ret;
    }
    /**
     * @returns {Promise<any>}
     */
    ownProfile() {
        const ret = wasm.wasmclient_ownProfile(this.__wbg_ptr);
        return ret;
    }
    /**
     * @param {string} room_id
     * @param {number} count
     * @returns {Promise<any>}
     */
    paginateBackwards(room_id, count) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_paginateBackwards(this.__wbg_ptr, ptr0, len0, count);
        return ret;
    }
    /**
     * @param {string} room_id
     * @param {number} count
     * @returns {Promise<any>}
     */
    paginateForwards(room_id, count) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_paginateForwards(this.__wbg_ptr, ptr0, len0, count);
        return ret;
    }
    /**
     * @param {string | null | undefined} server
     * @param {string | null | undefined} search
     * @param {number} limit
     * @param {string | null} [since]
     * @returns {Promise<any>}
     */
    publicRooms(server, search, limit, since) {
        var ptr0 = isLikeNone(server) ? 0 : passStringToWasm0(server, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        var len0 = WASM_VECTOR_LEN;
        var ptr1 = isLikeNone(search) ? 0 : passStringToWasm0(search, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        var len1 = WASM_VECTOR_LEN;
        var ptr2 = isLikeNone(since) ? 0 : passStringToWasm0(since, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        var len2 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_publicRooms(this.__wbg_ptr, ptr0, len0, ptr1, len1, limit, ptr2, len2);
        return ret;
    }
    /**
     * @param {string} room_id
     * @param {string} alias
     * @returns {Promise<boolean>}
     */
    publishRoomAlias(room_id, alias) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(alias, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_publishRoomAlias(this.__wbg_ptr, ptr0, len0, ptr1, len1);
        return ret;
    }
    /**
     * @param {string} room_id
     * @param {string} event_id
     * @param {string} emoji
     * @param {string | null} [shortcode]
     * @returns {Promise<any>}
     */
    react(room_id, event_id, emoji, shortcode) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(event_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        const ptr2 = passStringToWasm0(emoji, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len2 = WASM_VECTOR_LEN;
        var ptr3 = isLikeNone(shortcode) ? 0 : passStringToWasm0(shortcode, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        var len3 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_react(this.__wbg_ptr, ptr0, len0, ptr1, len1, ptr2, len2, ptr3, len3);
        return ret;
    }
    /**
     * @param {string} room_id
     * @param {string[]} event_ids
     * @returns {Promise<any>}
     */
    reactionsBatch(room_id, event_ids) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArrayJsValueToWasm0(event_ids, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_reactionsBatch(this.__wbg_ptr, ptr0, len0, ptr1, len1);
        return ret;
    }
    /**
     * @param {string} room_id
     * @param {string} event_id
     * @returns {Promise<any>}
     */
    reactionsForEvent(room_id, event_id) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(event_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_reactionsForEvent(this.__wbg_ptr, ptr0, len0, ptr1, len1);
        return ret;
    }
    /**
     * @returns {Promise<any>}
     */
    recentEmoji() {
        const ret = wasm.wasmclient_recentEmoji(this.__wbg_ptr);
        return ret;
    }
    /**
     * @param {string} room_id
     * @param {number} limit
     * @returns {Promise<any>}
     */
    recentEvents(room_id, limit) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_recentEvents(this.__wbg_ptr, ptr0, len0, limit);
        return ret;
    }
    /**
     * @param {string} emoji
     * @returns {Promise<any>}
     */
    recordEmojiUse(emoji) {
        const ptr0 = passStringToWasm0(emoji, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_recordEmojiUse(this.__wbg_ptr, ptr0, len0);
        return ret;
    }
    /**
     * @param {string} recovery_key
     * @returns {Promise<boolean>}
     */
    recoverWithKey(recovery_key) {
        const ptr0 = passStringToWasm0(recovery_key, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_recoverWithKey(this.__wbg_ptr, ptr0, len0);
        return ret;
    }
    /**
     * @param {string} room_id
     * @param {string} event_id
     * @param {string | null} [reason]
     * @returns {Promise<any>}
     */
    redact(room_id, event_id, reason) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(event_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        var ptr2 = isLikeNone(reason) ? 0 : passStringToWasm0(reason, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        var len2 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_redact(this.__wbg_ptr, ptr0, len0, ptr1, len1, ptr2, len2);
        return ret;
    }
    /**
     * @returns {Promise<any>}
     */
    removeAvatar() {
        const ret = wasm.wasmclient_removeAvatar(this.__wbg_ptr);
        return ret;
    }
    /**
     * @param {string} room_id
     * @param {string} state_key
     * @returns {Promise<any>}
     */
    removeImagePack(room_id, state_key) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(state_key, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_removeImagePack(this.__wbg_ptr, ptr0, len0, ptr1, len1);
        return ret;
    }
    /**
     * @param {string} keyword
     * @returns {Promise<any>}
     */
    removeNotificationKeyword(keyword) {
        const ptr0 = passStringToWasm0(keyword, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_removeNotificationKeyword(this.__wbg_ptr, ptr0, len0);
        return ret;
    }
    /**
     * @param {string} tag
     * @param {string} name
     * @returns {Promise<any>}
     */
    renameSection(tag, name) {
        const ptr0 = passStringToWasm0(tag, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(name, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_renameSection(this.__wbg_ptr, ptr0, len0, ptr1, len1);
        return ret;
    }
    /**
     * @param {string} room_id
     * @param {string} in_reply_to
     * @param {string} body
     * @param {string | null} [formatted_body]
     * @returns {Promise<any>}
     */
    reply(room_id, in_reply_to, body, formatted_body) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(in_reply_to, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        const ptr2 = passStringToWasm0(body, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len2 = WASM_VECTOR_LEN;
        var ptr3 = isLikeNone(formatted_body) ? 0 : passStringToWasm0(formatted_body, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        var len3 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_reply(this.__wbg_ptr, ptr0, len0, ptr1, len1, ptr2, len2, ptr3, len3);
        return ret;
    }
    /**
     * @param {string} room_id
     * @param {string} event_id
     * @param {number | null} [score]
     * @param {string | null} [reason]
     * @returns {Promise<boolean>}
     */
    reportContent(room_id, event_id, score, reason) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(event_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        var ptr2 = isLikeNone(reason) ? 0 : passStringToWasm0(reason, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        var len2 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_reportContent(this.__wbg_ptr, ptr0, len0, ptr1, len1, isLikeNone(score) ? Number.MAX_SAFE_INTEGER : (score) >> 0, ptr2, len2);
        return ret;
    }
    /**
     * @param {string} room_id
     * @param {string | null} [reason]
     * @returns {Promise<any>}
     */
    reportRoom(room_id, reason) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        var ptr1 = isLikeNone(reason) ? 0 : passStringToWasm0(reason, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        var len1 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_reportRoom(this.__wbg_ptr, ptr0, len0, ptr1, len1);
        return ret;
    }
    /**
     * @returns {Promise<any>}
     */
    resetRecoveryKey() {
        const ret = wasm.wasmclient_resetRecoveryKey(this.__wbg_ptr);
        return ret;
    }
    /**
     * @param {string} id_or_alias
     * @returns {Promise<any>}
     */
    resolveRoomId(id_or_alias) {
        const ptr0 = passStringToWasm0(id_or_alias, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_resolveRoomId(this.__wbg_ptr, ptr0, len0);
        return ret;
    }
    /**
     * @param {string} room_id
     * @param {string} txn_id
     * @returns {Promise<any>}
     */
    retryByTxn(room_id, txn_id) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(txn_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_retryByTxn(this.__wbg_ptr, ptr0, len0, ptr1, len1);
        return ret;
    }
    /**
     * @param {string} room_id
     * @returns {Promise<any>}
     */
    roomActionState(room_id) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_roomActionState(this.__wbg_ptr, ptr0, len0);
        return ret;
    }
    /**
     * @param {string} room_id
     * @returns {Promise<any>}
     */
    roomAliases(room_id) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_roomAliases(this.__wbg_ptr, ptr0, len0);
        return ret;
    }
    /**
     * @param {string} room_id
     * @returns {Promise<any>}
     */
    roomCallState(room_id) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_roomCallState(this.__wbg_ptr, ptr0, len0);
        return ret;
    }
    /**
     * @param {string} room_id
     * @returns {Promise<any>}
     */
    roomDirectoryVisibility(room_id) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_roomDirectoryVisibility(this.__wbg_ptr, ptr0, len0);
        return ret;
    }
    /**
     * @param {string} room_id
     * @returns {Promise<any>}
     */
    roomHistoryVisibility(room_id) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_roomHistoryVisibility(this.__wbg_ptr, ptr0, len0);
        return ret;
    }
    /**
     * @param {string} room_id
     * @returns {Promise<any>}
     */
    roomInfoSnapshot(room_id) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_roomInfoSnapshot(this.__wbg_ptr, ptr0, len0);
        return ret;
    }
    /**
     * @param {string} room_id
     * @returns {Promise<string | undefined>}
     */
    roomInviter(room_id) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_roomInviter(this.__wbg_ptr, ptr0, len0);
        return ret;
    }
    /**
     * @param {string} room_id
     * @returns {Promise<any>}
     */
    roomJoinRuleAllowList(room_id) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_roomJoinRuleAllowList(this.__wbg_ptr, ptr0, len0);
        return ret;
    }
    /**
     * @param {string} room_id
     * @returns {Promise<any>}
     */
    roomJoinRule(room_id) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_roomJoinRule(this.__wbg_ptr, ptr0, len0);
        return ret;
    }
    /**
     * @param {number} token
     * @param {boolean} unread_only
     * @returns {boolean}
     */
    roomListSetUnreadOnly(token, unread_only) {
        const ret = wasm.wasmclient_roomListSetUnreadOnly(this.__wbg_ptr, token, unread_only);
        return ret !== 0;
    }
    /**
     * @param {number} token
     * @param {number[]} range
     * @param {number} threshold
     * @returns {any}
     */
    roomListUpdateVisibleRange(token, range, threshold) {
        const ret = wasm.wasmclient_roomListUpdateVisibleRange(this.__wbg_ptr, token, range, threshold);
        return ret;
    }
    /**
     * @param {string} room_id
     * @returns {Promise<any>}
     */
    roomNotificationMode(room_id) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_roomNotificationMode(this.__wbg_ptr, ptr0, len0);
        return ret;
    }
    /**
     * @param {string} room_id
     * @returns {Promise<any>}
     */
    roomParentSpaces(room_id) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_roomParentSpaces(this.__wbg_ptr, ptr0, len0);
        return ret;
    }
    /**
     * @param {string} room_id
     * @returns {Promise<any>}
     */
    roomPowerLevels(room_id) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_roomPowerLevels(this.__wbg_ptr, ptr0, len0);
        return ret;
    }
    /**
     * @param {string} room_id
     * @returns {Promise<any>}
     */
    roomPredecessor(room_id) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_roomPredecessor(this.__wbg_ptr, ptr0, len0);
        return ret;
    }
    /**
     * @param {string} id_or_alias
     * @param {string[]} via
     * @returns {Promise<any>}
     */
    roomPreview(id_or_alias, via) {
        const ptr0 = passStringToWasm0(id_or_alias, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArrayJsValueToWasm0(via, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_roomPreview(this.__wbg_ptr, ptr0, len0, ptr1, len1);
        return ret;
    }
    /**
     * @param {string} room_id
     * @returns {Promise<any>}
     */
    roomProfile(room_id) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_roomProfile(this.__wbg_ptr, ptr0, len0);
        return ret;
    }
    /**
     * @param {string} room_id
     * @returns {Promise<any>}
     */
    roomStorageUsage(room_id) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_roomStorageUsage(this.__wbg_ptr, ptr0, len0);
        return ret;
    }
    /**
     * @param {string} room_id
     * @returns {Promise<any>}
     */
    roomSuccessor(room_id) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_roomSuccessor(this.__wbg_ptr, ptr0, len0);
        return ret;
    }
    /**
     * @param {string} room_id
     * @returns {Promise<any>}
     */
    roomTags(room_id) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_roomTags(this.__wbg_ptr, ptr0, len0);
        return ret;
    }
    /**
     * @param {string} room_id
     * @returns {Promise<any>}
     */
    roomUnreadStats(room_id) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_roomUnreadStats(this.__wbg_ptr, ptr0, len0);
        return ret;
    }
    /**
     * @param {string} room_id
     * @returns {Promise<any>}
     */
    roomUpgradeLinks(room_id) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_roomUpgradeLinks(this.__wbg_ptr, ptr0, len0);
        return ret;
    }
    /**
     * @returns {Promise<any>}
     */
    rooms() {
        const ret = wasm.wasmclient_rooms(this.__wbg_ptr);
        return ret;
    }
    /**
     * @param {string} room_id
     * @param {string} write_json
     * @returns {Promise<any>}
     */
    saveImagePack(room_id, write_json) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(write_json, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_saveImagePack(this.__wbg_ptr, ptr0, len0, ptr1, len1);
        return ret;
    }
    /**
     * @param {string} _room_id
     * @param {string} _query
     * @param {number} _limit
     * @param {number | null} [_offset]
     * @returns {Promise<any>}
     */
    searchRoom(_room_id, _query, _limit, _offset) {
        const ptr0 = passStringToWasm0(_room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(_query, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_searchRoom(this.__wbg_ptr, ptr0, len0, ptr1, len1, _limit, isLikeNone(_offset) ? Number.MAX_SAFE_INTEGER : (_offset) >>> 0);
        return ret;
    }
    /**
     * @param {string} search_term
     * @param {number} limit
     * @returns {Promise<any>}
     */
    searchUsers(search_term, limit) {
        const ptr0 = passStringToWasm0(search_term, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_searchUsers(this.__wbg_ptr, ptr0, len0, limit);
        return ret;
    }
    /**
     * @param {string} room_id
     * @param {string} event_id
     * @param {number} limit
     * @returns {Promise<any>}
     */
    seenByForEvent(room_id, event_id, limit) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(event_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_seenByForEvent(this.__wbg_ptr, ptr0, len0, ptr1, len1, limit);
        return ret;
    }
    /**
     * @param {string} room_id
     * @param {string} filename
     * @param {string} mime
     * @param {Uint8Array} data
     * @param {string | null} [caption]
     * @param {string | null} [formatted_caption]
     * @param {string | null} [reply_to_event_id]
     * @param {bigint | null} [voice_duration_ms]
     * @param {Float32Array | null} [voice_waveform]
     * @param {boolean | null} [is_voice]
     * @returns {Promise<any>}
     */
    sendAttachmentBytes(room_id, filename, mime, data, caption, formatted_caption, reply_to_event_id, voice_duration_ms, voice_waveform, is_voice) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(filename, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        const ptr2 = passStringToWasm0(mime, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len2 = WASM_VECTOR_LEN;
        const ptr3 = passArray8ToWasm0(data, wasm.__wbindgen_malloc);
        const len3 = WASM_VECTOR_LEN;
        var ptr4 = isLikeNone(caption) ? 0 : passStringToWasm0(caption, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        var len4 = WASM_VECTOR_LEN;
        var ptr5 = isLikeNone(formatted_caption) ? 0 : passStringToWasm0(formatted_caption, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        var len5 = WASM_VECTOR_LEN;
        var ptr6 = isLikeNone(reply_to_event_id) ? 0 : passStringToWasm0(reply_to_event_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        var len6 = WASM_VECTOR_LEN;
        var ptr7 = isLikeNone(voice_waveform) ? 0 : passArrayF32ToWasm0(voice_waveform, wasm.__wbindgen_malloc);
        var len7 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_sendAttachmentBytes(this.__wbg_ptr, ptr0, len0, ptr1, len1, ptr2, len2, ptr3, len3, ptr4, len4, ptr5, len5, ptr6, len6, !isLikeNone(voice_duration_ms), isLikeNone(voice_duration_ms) ? BigInt(0) : voice_duration_ms, ptr7, len7, isLikeNone(is_voice) ? 0xFFFFFF : is_voice ? 1 : 0);
        return ret;
    }
    /**
     * @param {string} room_id
     * @param {string} att_json
     * @param {string | null} [body]
     * @param {string | null} [formatted_body]
     * @returns {Promise<any>}
     */
    sendExistingAttachment(room_id, att_json, body, formatted_body) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(att_json, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        var ptr2 = isLikeNone(body) ? 0 : passStringToWasm0(body, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        var len2 = WASM_VECTOR_LEN;
        var ptr3 = isLikeNone(formatted_body) ? 0 : passStringToWasm0(formatted_body, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        var len3 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_sendExistingAttachment(this.__wbg_ptr, ptr0, len0, ptr1, len1, ptr2, len2, ptr3, len3);
        return ret;
    }
    /**
     * @param {string} room_id
     * @param {string} geo_uri
     * @returns {Promise<any>}
     */
    sendLiveLocation(room_id, geo_uri) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(geo_uri, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_sendLiveLocation(this.__wbg_ptr, ptr0, len0, ptr1, len1);
        return ret;
    }
    /**
     * @param {string} room_id
     * @param {string} body
     * @param {string | null} [formatted_body]
     * @returns {Promise<any>}
     */
    sendMessage(room_id, body, formatted_body) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(body, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        var ptr2 = isLikeNone(formatted_body) ? 0 : passStringToWasm0(formatted_body, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        var len2 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_sendMessage(this.__wbg_ptr, ptr0, len0, ptr1, len1, ptr2, len2);
        return ret;
    }
    /**
     * @param {string} room_id
     * @param {string} poll_event_id
     * @returns {Promise<any>}
     */
    sendPollEnd(room_id, poll_event_id) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(poll_event_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_sendPollEnd(this.__wbg_ptr, ptr0, len0, ptr1, len1);
        return ret;
    }
    /**
     * @param {string} room_id
     * @param {string} poll_event_id
     * @param {string[]} answers
     * @returns {Promise<boolean>}
     */
    sendPollResponse(room_id, poll_event_id, answers) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(poll_event_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        const ptr2 = passArrayJsValueToWasm0(answers, wasm.__wbindgen_malloc);
        const len2 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_sendPollResponse(this.__wbg_ptr, ptr0, len0, ptr1, len1, ptr2, len2);
        return ret;
    }
    /**
     * @param {string} room_id
     * @param {string} question
     * @param {string[]} answers
     * @param {string} kind
     * @param {number} max_selections
     * @returns {Promise<any>}
     */
    sendPollStart(room_id, question, answers, kind, max_selections) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(question, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        const ptr2 = passArrayJsValueToWasm0(answers, wasm.__wbindgen_malloc);
        const len2 = WASM_VECTOR_LEN;
        const ptr3 = passStringToWasm0(kind, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len3 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_sendPollStart(this.__wbg_ptr, ptr0, len0, ptr1, len1, ptr2, len2, ptr3, len3, max_selections);
        return ret;
    }
    /**
     * @param {boolean} enabled
     * @returns {Promise<any>}
     */
    sendQueueSetEnabled(enabled) {
        const ret = wasm.wasmclient_sendQueueSetEnabled(this.__wbg_ptr, enabled);
        return ret;
    }
    /**
     * @param {string} room_id
     * @param {string} geo_uri
     * @param {string | null} [body]
     * @returns {Promise<any>}
     */
    sendStaticLocation(room_id, geo_uri, body) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(geo_uri, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        var ptr2 = isLikeNone(body) ? 0 : passStringToWasm0(body, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        var len2 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_sendStaticLocation(this.__wbg_ptr, ptr0, len0, ptr1, len1, ptr2, len2);
        return ret;
    }
    /**
     * @param {string} room_id
     * @param {string} mxc_url
     * @param {string} body
     * @param {string | null} [info_json]
     * @param {string | null} [thread_root_event_id]
     * @returns {Promise<any>}
     */
    sendStickerMxc(room_id, mxc_url, body, info_json, thread_root_event_id) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(mxc_url, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        const ptr2 = passStringToWasm0(body, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len2 = WASM_VECTOR_LEN;
        var ptr3 = isLikeNone(info_json) ? 0 : passStringToWasm0(info_json, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        var len3 = WASM_VECTOR_LEN;
        var ptr4 = isLikeNone(thread_root_event_id) ? 0 : passStringToWasm0(thread_root_event_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        var len4 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_sendStickerMxc(this.__wbg_ptr, ptr0, len0, ptr1, len1, ptr2, len2, ptr3, len3, ptr4, len4);
        return ret;
    }
    /**
     * @param {string} room_id
     * @param {string} root_event_id
     * @param {string} body
     * @param {string | null} [reply_to_event_id]
     * @param {string | null} [latest_event_id]
     * @param {string | null} [formatted_body]
     * @returns {Promise<any>}
     */
    sendThreadText(room_id, root_event_id, body, reply_to_event_id, latest_event_id, formatted_body) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(root_event_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        const ptr2 = passStringToWasm0(body, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len2 = WASM_VECTOR_LEN;
        var ptr3 = isLikeNone(reply_to_event_id) ? 0 : passStringToWasm0(reply_to_event_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        var len3 = WASM_VECTOR_LEN;
        var ptr4 = isLikeNone(latest_event_id) ? 0 : passStringToWasm0(latest_event_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        var len4 = WASM_VECTOR_LEN;
        var ptr5 = isLikeNone(formatted_body) ? 0 : passStringToWasm0(formatted_body, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        var len5 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_sendThreadText(this.__wbg_ptr, ptr0, len0, ptr1, len1, ptr2, len2, ptr3, len3, ptr4, len4, ptr5, len5);
        return ret;
    }
    /**
     * @param {string} event_type
     * @param {string} content_json
     * @returns {Promise<any>}
     */
    setAccountData(event_type, content_json) {
        const ptr0 = passStringToWasm0(event_type, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(content_json, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_setAccountData(this.__wbg_ptr, ptr0, len0, ptr1, len1);
        return ret;
    }
    /**
     * @param {boolean} metered
     * @param {boolean} roaming
     * @returns {any}
     */
    setAutoDownloadNetwork(metered, roaming) {
        const ret = wasm.wasmclient_setAutoDownloadNetwork(this.__wbg_ptr, metered, roaming);
        return ret;
    }
    /**
     * @param {string} rules_json
     * @returns {any}
     */
    setAutoDownloadRules(rules_json) {
        const ptr0 = passStringToWasm0(rules_json, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_setAutoDownloadRules(this.__wbg_ptr, ptr0, len0);
        return ret;
    }
    /**
     * @param {Uint8Array} bytes
     * @param {string} mime
     * @returns {Promise<any>}
     */
    setAvatarBytes(bytes, mime) {
        const ptr0 = passArray8ToWasm0(bytes, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(mime, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_setAvatarBytes(this.__wbg_ptr, ptr0, len0, ptr1, len1);
        return ret;
    }
    /**
     * @param {boolean} is_encrypted
     * @param {boolean} is_one_to_one
     * @param {string} mode
     * @returns {Promise<any>}
     */
    setDefaultRoomNotificationMode(is_encrypted, is_one_to_one, mode) {
        const ptr0 = passStringToWasm0(mode, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_setDefaultRoomNotificationMode(this.__wbg_ptr, is_encrypted, is_one_to_one, ptr0, len0);
        return ret;
    }
    /**
     * @param {string | null} [name]
     * @returns {Promise<any>}
     */
    setDisplayName(name) {
        var ptr0 = isLikeNone(name) ? 0 : passStringToWasm0(name, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        var len0 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_setDisplayName(this.__wbg_ptr, ptr0, len0);
        return ret;
    }
    /**
     * @param {string} room_id
     * @param {string} state_key
     * @param {boolean} enabled
     * @returns {Promise<any>}
     */
    setImagePackEnabled(room_id, state_key, enabled) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(state_key, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_setImagePackEnabled(this.__wbg_ptr, ptr0, len0, ptr1, len1, enabled);
        return ret;
    }
    /**
     * @param {boolean} blocked
     * @returns {Promise<any>}
     */
    setInviteBlocked(blocked) {
        const ret = wasm.wasmclient_setInviteBlocked(this.__wbg_ptr, blocked);
        return ret;
    }
    /**
     * @param {boolean} enabled
     * @returns {Promise<boolean>}
     */
    setKeyBackupEnabled(enabled) {
        const ret = wasm.wasmclient_setKeyBackupEnabled(this.__wbg_ptr, enabled);
        return ret;
    }
    /**
     * @param {string} room_id
     * @param {boolean} unread
     * @returns {Promise<any>}
     */
    setMarkUnread(room_id, unread) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_setMarkUnread(this.__wbg_ptr, ptr0, len0, unread);
        return ret;
    }
    /**
     * @param {string} previews
     * @returns {Promise<any>}
     */
    setMediaPreviewConfig(previews) {
        const ptr0 = passStringToWasm0(previews, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_setMediaPreviewConfig(this.__wbg_ptr, ptr0, len0);
        return ret;
    }
    /**
     * @param {number | null} [max_cache_size_bytes]
     * @param {number | null} [max_file_size_bytes]
     * @param {number | null} [last_access_expiry_days]
     * @returns {Promise<any>}
     */
    setMediaRetentionPolicy(max_cache_size_bytes, max_file_size_bytes, last_access_expiry_days) {
        const ret = wasm.wasmclient_setMediaRetentionPolicy(this.__wbg_ptr, !isLikeNone(max_cache_size_bytes), isLikeNone(max_cache_size_bytes) ? 0 : max_cache_size_bytes, !isLikeNone(max_file_size_bytes), isLikeNone(max_file_size_bytes) ? 0 : max_file_size_bytes, !isLikeNone(last_access_expiry_days), isLikeNone(last_access_expiry_days) ? 0 : last_access_expiry_days);
        return ret;
    }
    /**
     * @param {string} room_id
     * @param {string[]} event_ids
     * @returns {Promise<any>}
     */
    setPinnedEvents(room_id, event_ids) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_setPinnedEvents(this.__wbg_ptr, ptr0, len0, event_ids);
        return ret;
    }
    /**
     * @param {string} presence
     * @param {string | null} [status]
     * @returns {Promise<any>}
     */
    setPresence(presence, status) {
        const ptr0 = passStringToWasm0(presence, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        var ptr1 = isLikeNone(status) ? 0 : passStringToWasm0(status, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        var len1 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_setPresence(this.__wbg_ptr, ptr0, len0, ptr1, len1);
        return ret;
    }
    /**
     * @param {string} name
     * @param {string} value
     * @returns {Promise<any>}
     */
    setProfileField(name, value) {
        const ptr0 = passStringToWasm0(name, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(value, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_setProfileField(this.__wbg_ptr, ptr0, len0, ptr1, len1);
        return ret;
    }
    /**
     * @param {string} kind
     * @param {string} rule_id
     * @param {boolean} enabled
     * @returns {Promise<any>}
     */
    setPushRuleEnabled(kind, rule_id, enabled) {
        const ptr0 = passStringToWasm0(kind, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(rule_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_setPushRuleEnabled(this.__wbg_ptr, ptr0, len0, ptr1, len1, enabled);
        return ret;
    }
    /**
     * @param {boolean} enabled
     * @returns {Promise<any>}
     */
    setReactionNotificationsEnabled(enabled) {
        const ret = wasm.wasmclient_setReactionNotificationsEnabled(this.__wbg_ptr, enabled);
        return ret;
    }
    /**
     * @param {string} room_id
     * @param {string | null | undefined} alias
     * @param {string[]} alt_aliases
     * @returns {Promise<boolean>}
     */
    setRoomCanonicalAlias(room_id, alias, alt_aliases) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        var ptr1 = isLikeNone(alias) ? 0 : passStringToWasm0(alias, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        var len1 = WASM_VECTOR_LEN;
        const ptr2 = passArrayJsValueToWasm0(alt_aliases, wasm.__wbindgen_malloc);
        const len2 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_setRoomCanonicalAlias(this.__wbg_ptr, ptr0, len0, ptr1, len1, ptr2, len2);
        return ret;
    }
    /**
     * @param {string} room_id
     * @param {string} visibility
     * @returns {Promise<boolean>}
     */
    setRoomDirectoryVisibility(room_id, visibility) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(visibility, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_setRoomDirectoryVisibility(this.__wbg_ptr, ptr0, len0, ptr1, len1);
        return ret;
    }
    /**
     * @param {string} room_id
     * @param {boolean} fav
     * @returns {Promise<any>}
     */
    setRoomFavourite(room_id, fav) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_setRoomFavourite(this.__wbg_ptr, ptr0, len0, fav);
        return ret;
    }
    /**
     * @param {string} room_id
     * @param {string} visibility
     * @returns {Promise<boolean>}
     */
    setRoomHistoryVisibility(room_id, visibility) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(visibility, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_setRoomHistoryVisibility(this.__wbg_ptr, ptr0, len0, ptr1, len1);
        return ret;
    }
    /**
     * @param {string} room_id
     * @param {string} rule
     * @param {string[] | null} [allowed_room_ids]
     * @returns {Promise<boolean>}
     */
    setRoomJoinRule(room_id, rule, allowed_room_ids) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(rule, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        var ptr2 = isLikeNone(allowed_room_ids) ? 0 : passArrayJsValueToWasm0(allowed_room_ids, wasm.__wbindgen_malloc);
        var len2 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_setRoomJoinRule(this.__wbg_ptr, ptr0, len0, ptr1, len1, ptr2, len2);
        return ret;
    }
    /**
     * @param {string} room_id
     * @param {boolean} low
     * @returns {Promise<any>}
     */
    setRoomLowPriority(room_id, low) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_setRoomLowPriority(this.__wbg_ptr, ptr0, len0, low);
        return ret;
    }
    /**
     * @param {string} room_id
     * @param {string} name
     * @returns {Promise<any>}
     */
    setRoomName(room_id, name) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(name, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_setRoomName(this.__wbg_ptr, ptr0, len0, ptr1, len1);
        return ret;
    }
    /**
     * @param {string} room_id
     * @param {string} mode
     * @returns {Promise<boolean>}
     */
    setRoomNotificationMode(room_id, mode) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(mode, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_setRoomNotificationMode(this.__wbg_ptr, ptr0, len0, ptr1, len1);
        return ret;
    }
    /**
     * @param {string} room_id
     * @param {string | null} [tag]
     * @returns {Promise<any>}
     */
    setRoomSection(room_id, tag) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        var ptr1 = isLikeNone(tag) ? 0 : passStringToWasm0(tag, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        var len1 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_setRoomSection(this.__wbg_ptr, ptr0, len0, ptr1, len1);
        return ret;
    }
    /**
     * @param {string} room_id
     * @param {string} topic
     * @returns {Promise<any>}
     */
    setRoomTopic(room_id, topic) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(topic, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_setRoomTopic(this.__wbg_ptr, ptr0, len0, ptr1, len1);
        return ret;
    }
    /**
     * @param {string} room_id
     * @param {boolean} typing
     * @returns {Promise<any>}
     */
    setTyping(room_id, typing) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_setTyping(this.__wbg_ptr, ptr0, len0, typing);
        return ret;
    }
    /**
     * @returns {Promise<any>}
     */
    setupRecovery() {
        const ret = wasm.wasmclient_setupRecovery(this.__wbg_ptr);
        return ret;
    }
    /**
     * @param {string} space_id
     * @param {string} child_room_id
     * @param {string | null} [order]
     * @param {boolean | null} [suggested]
     * @returns {Promise<any>}
     */
    spaceAddChild(space_id, child_room_id, order, suggested) {
        const ptr0 = passStringToWasm0(space_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(child_room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        var ptr2 = isLikeNone(order) ? 0 : passStringToWasm0(order, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        var len2 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_spaceAddChild(this.__wbg_ptr, ptr0, len0, ptr1, len1, ptr2, len2, isLikeNone(suggested) ? 0xFFFFFF : suggested ? 1 : 0);
        return ret;
    }
    /**
     * @param {string} space_id
     * @param {string | null | undefined} from
     * @param {number} limit
     * @param {number | null | undefined} max_depth
     * @param {boolean} suggested_only
     * @returns {Promise<any>}
     */
    spaceHierarchy(space_id, from, limit, max_depth, suggested_only) {
        const ptr0 = passStringToWasm0(space_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        var ptr1 = isLikeNone(from) ? 0 : passStringToWasm0(from, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        var len1 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_spaceHierarchy(this.__wbg_ptr, ptr0, len0, ptr1, len1, limit, isLikeNone(max_depth) ? Number.MAX_SAFE_INTEGER : (max_depth) >>> 0, suggested_only);
        return ret;
    }
    /**
     * @param {string} space_id
     * @param {string} user_id
     * @returns {Promise<any>}
     */
    spaceInviteUser(space_id, user_id) {
        const ptr0 = passStringToWasm0(space_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(user_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_spaceInviteUser(this.__wbg_ptr, ptr0, len0, ptr1, len1);
        return ret;
    }
    /**
     * @param {string} space_id
     * @param {string} child_room_id
     * @returns {Promise<any>}
     */
    spaceRemoveChild(space_id, child_room_id) {
        const ptr0 = passStringToWasm0(space_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(child_room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_spaceRemoveChild(this.__wbg_ptr, ptr0, len0, ptr1, len1);
        return ret;
    }
    /**
     * @returns {Promise<any>}
     */
    spaceUnreadCounts() {
        const ret = wasm.wasmclient_spaceUnreadCounts(this.__wbg_ptr);
        return ret;
    }
    /**
     * @param {Function} on_invite
     * @returns {number}
     */
    startCallInbox(on_invite) {
        const ret = wasm.wasmclient_startCallInbox(this.__wbg_ptr, on_invite);
        return ret;
    }
    /**
     * @param {string} target_device_id
     * @param {Function} on_event
     * @returns {Promise<string>}
     */
    startDeviceVerification(target_device_id, on_event) {
        const ptr0 = passStringToWasm0(target_device_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_startDeviceVerification(this.__wbg_ptr, ptr0, len0, on_event);
        return ret;
    }
    /**
     * @param {string} room_id
     * @param {string | null | undefined} element_call_url
     * @param {string | null | undefined} parent_url
     * @param {string} intent
     * @param {string | null | undefined} language_tag
     * @param {string | null | undefined} theme
     * @param {any} observer
     * @returns {Promise<any>}
     */
    startElementCall(room_id, element_call_url, parent_url, intent, language_tag, theme, observer) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        var ptr1 = isLikeNone(element_call_url) ? 0 : passStringToWasm0(element_call_url, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        var len1 = WASM_VECTOR_LEN;
        var ptr2 = isLikeNone(parent_url) ? 0 : passStringToWasm0(parent_url, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        var len2 = WASM_VECTOR_LEN;
        const ptr3 = passStringToWasm0(intent, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len3 = WASM_VECTOR_LEN;
        var ptr4 = isLikeNone(language_tag) ? 0 : passStringToWasm0(language_tag, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        var len4 = WASM_VECTOR_LEN;
        var ptr5 = isLikeNone(theme) ? 0 : passStringToWasm0(theme, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        var len5 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_startElementCall(this.__wbg_ptr, ptr0, len0, ptr1, len1, ptr2, len2, ptr3, len3, ptr4, len4, ptr5, len5, observer);
        return ret;
    }
    /**
     * @param {string} room_id
     * @param {number} duration_ms
     * @returns {Promise<any>}
     */
    startLiveLocation(room_id, duration_ms) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_startLiveLocation(this.__wbg_ptr, ptr0, len0, duration_ms);
        return ret;
    }
    /**
     * @param {Function} on_state
     */
    startSupervisedSync(on_state) {
        wasm.wasmclient_startSupervisedSync(this.__wbg_ptr, on_state);
    }
    /**
     * @param {string} user_id
     * @param {Function} on_event
     * @returns {Promise<string>}
     */
    startUserVerification(user_id, on_event) {
        const ptr0 = passStringToWasm0(user_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_startUserVerification(this.__wbg_ptr, ptr0, len0, on_event);
        return ret;
    }
    /**
     * @param {Function} on_request
     * @param {Function} on_error
     * @returns {number}
     */
    startVerificationInbox(on_request, on_error) {
        const ret = wasm.wasmclient_startVerificationInbox(this.__wbg_ptr, on_request, on_error);
        return ret;
    }
    /**
     * @param {number} sub_id
     * @returns {boolean}
     */
    stopCallInbox(sub_id) {
        const ret = wasm.wasmclient_stopCallInbox(this.__wbg_ptr, sub_id);
        return ret !== 0;
    }
    /**
     * @param {number} session_id
     * @returns {boolean}
     */
    stopElementCall(session_id) {
        const ret = wasm.wasmclient_stopElementCall(this.__wbg_ptr, session_id);
        return ret !== 0;
    }
    /**
     * @param {string} room_id
     * @returns {Promise<any>}
     */
    stopLiveLocation(room_id) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_stopLiveLocation(this.__wbg_ptr, ptr0, len0);
        return ret;
    }
    /**
     * @param {string[]} room_ids
     * @returns {any}
     */
    subscribeRooms(room_ids) {
        const ret = wasm.wasmclient_subscribeRooms(this.__wbg_ptr, room_ids);
        return ret;
    }
    /**
     * @param {string[]} bases
     * @param {string[]} taken
     * @returns {Promise<any>}
     */
    suggestImageShortcodes(bases, taken) {
        const ptr0 = passArrayJsValueToWasm0(bases, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArrayJsValueToWasm0(taken, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_suggestImageShortcodes(this.__wbg_ptr, ptr0, len0, ptr1, len1);
        return ret;
    }
    /**
     * @param {string} room_id
     * @param {string} root_event_id
     * @param {string | null | undefined} from
     * @param {number} limit
     * @param {boolean} forward
     * @returns {Promise<any>}
     */
    threadReplies(room_id, root_event_id, from, limit, forward) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(root_event_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        var ptr2 = isLikeNone(from) ? 0 : passStringToWasm0(from, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        var len2 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_threadReplies(this.__wbg_ptr, ptr0, len0, ptr1, len1, ptr2, len2, limit, forward);
        return ret;
    }
    /**
     * @param {string} room_id
     * @param {string} root_event_id
     * @param {number} per_page
     * @param {number} max_pages
     * @returns {Promise<any>}
     */
    threadSummary(room_id, root_event_id, per_page, max_pages) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(root_event_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_threadSummary(this.__wbg_ptr, ptr0, len0, ptr1, len1, per_page, max_pages);
        return ret;
    }
    /**
     * @param {string} att_json
     * @param {number} width
     * @param {number} height
     * @param {boolean} _use_crop
     * @param {boolean} animated
     * @returns {Promise<any>}
     */
    thumbnailToCache(att_json, width, height, _use_crop, animated) {
        const ptr0 = passStringToWasm0(att_json, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_thumbnailToCache(this.__wbg_ptr, ptr0, len0, width, height, _use_crop, animated);
        return ret;
    }
    /**
     * @param {string} room_id
     * @param {string} user_id
     * @param {string | null} [reason]
     * @returns {Promise<any>}
     */
    unbanUser(room_id, user_id, reason) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(user_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        var ptr2 = isLikeNone(reason) ? 0 : passStringToWasm0(reason, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        var len2 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_unbanUser(this.__wbg_ptr, ptr0, len0, ptr1, len1, ptr2, len2);
        return ret;
    }
    /**
     * @param {string} user_id
     * @returns {Promise<any>}
     */
    unignoreUser(user_id) {
        const ptr0 = passStringToWasm0(user_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_unignoreUser(this.__wbg_ptr, ptr0, len0);
        return ret;
    }
    /**
     * @param {number} sub_id
     * @returns {boolean}
     */
    unobserveBackupState(sub_id) {
        const ret = wasm.wasmclient_unobserveBackupState(this.__wbg_ptr, sub_id);
        return ret !== 0;
    }
    /**
     * @param {number} sub_id
     * @returns {boolean}
     */
    unobserveCallDecline(sub_id) {
        const ret = wasm.wasmclient_unobserveCallDecline(this.__wbg_ptr, sub_id);
        return ret !== 0;
    }
    /**
     * @param {number} sub_id
     * @returns {boolean}
     */
    unobserveLiveLocation(sub_id) {
        const ret = wasm.wasmclient_unobserveLiveLocation(this.__wbg_ptr, sub_id);
        return ret !== 0;
    }
    /**
     * @param {number} sub_id
     * @returns {boolean}
     */
    unobserveReceipts(sub_id) {
        const ret = wasm.wasmclient_unobserveReceipts(this.__wbg_ptr, sub_id);
        return ret !== 0;
    }
    /**
     * @param {number} sub_id
     * @returns {boolean}
     */
    unobserveRecoveryState(sub_id) {
        const ret = wasm.wasmclient_unobserveRecoveryState(this.__wbg_ptr, sub_id);
        return ret !== 0;
    }
    /**
     * @param {number} sub_id
     * @returns {boolean}
     */
    unobserveRoomCallState(sub_id) {
        const ret = wasm.wasmclient_unobserveRoomCallState(this.__wbg_ptr, sub_id);
        return ret !== 0;
    }
    /**
     * @param {number} sub_id
     * @returns {boolean}
     */
    unobserveRoomInfo(sub_id) {
        const ret = wasm.wasmclient_unobserveRoomInfo(this.__wbg_ptr, sub_id);
        return ret !== 0;
    }
    /**
     * @param {number} sub_id
     * @returns {boolean}
     */
    unobserveRoomList(sub_id) {
        const ret = wasm.wasmclient_unobserveRoomList(this.__wbg_ptr, sub_id);
        return ret !== 0;
    }
    /**
     * @param {number} id
     * @returns {boolean}
     */
    unobserveSends(id) {
        const ret = wasm.wasmclient_unobserveSends(this.__wbg_ptr, id);
        return ret !== 0;
    }
    /**
     * @param {number} sub_id
     * @returns {boolean}
     */
    unobserveTimeline(sub_id) {
        const ret = wasm.wasmclient_unobserveTimeline(this.__wbg_ptr, sub_id);
        return ret !== 0;
    }
    /**
     * @param {number} sub_id
     * @returns {boolean}
     */
    unobserveTyping(sub_id) {
        const ret = wasm.wasmclient_unobserveTyping(this.__wbg_ptr, sub_id);
        return ret !== 0;
    }
    /**
     * @param {number} sub_id
     * @returns {boolean}
     */
    unobserveVerificationInbox(sub_id) {
        const ret = wasm.wasmclient_unobserveVerificationInbox(this.__wbg_ptr, sub_id);
        return ret !== 0;
    }
    /**
     * @param {string} room_id
     * @param {string} alias
     * @returns {Promise<boolean>}
     */
    unpublishRoomAlias(room_id, alias) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(alias, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_unpublishRoomAlias(this.__wbg_ptr, ptr0, len0, ptr1, len1);
        return ret;
    }
    /**
     * @param {string} room_id
     * @param {string} user_id
     * @param {number} power_level
     * @returns {Promise<boolean>}
     */
    updatePowerLevelForUser(room_id, user_id, power_level) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(user_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_updatePowerLevelForUser(this.__wbg_ptr, ptr0, len0, ptr1, len1, power_level);
        return ret;
    }
    /**
     * @param {string} room_id
     * @param {string} new_version
     * @returns {Promise<any>}
     */
    upgradeRoom(room_id, new_version) {
        const ptr0 = passStringToWasm0(room_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(new_version, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_upgradeRoom(this.__wbg_ptr, ptr0, len0, ptr1, len1);
        return ret;
    }
    /**
     * @param {Uint8Array} bytes
     * @param {string} mime
     * @returns {Promise<any>}
     */
    uploadBytes(bytes, mime) {
        const ptr0 = passArray8ToWasm0(bytes, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(mime, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_uploadBytes(this.__wbg_ptr, ptr0, len0, ptr1, len1);
        return ret;
    }
    /**
     * @param {Uint8Array} bytes
     * @param {string} mime
     * @returns {Promise<any>}
     */
    uploadPackImageBytes(bytes, mime) {
        const ptr0 = passArray8ToWasm0(bytes, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(mime, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.wasmclient_uploadPackImageBytes(this.__wbg_ptr, ptr0, len0, ptr1, len1);
        return ret;
    }
    /**
     * @returns {string | undefined}
     */
    whoami() {
        const ret = wasm.wasmclient_whoami(this.__wbg_ptr);
        let v1;
        if (ret[0] !== 0) {
            v1 = getStringFromWasm0(ret[0], ret[1]);
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v1;
    }
}
if (Symbol.dispose) WasmClient.prototype[Symbol.dispose] = WasmClient.prototype.free;

/**
 * @param {Uint8Array} data
 * @returns {string}
 */
export function base64Encode(data) {
    let deferred3_0;
    let deferred3_1;
    try {
        const ptr0 = passArray8ToWasm0(data, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.base64Encode(ptr0, len0);
        var ptr2 = ret[0];
        var len2 = ret[1];
        if (ret[3]) {
            ptr2 = 0; len2 = 0;
            throw takeFromExternrefTable0(ret[2]);
        }
        deferred3_0 = ptr2;
        deferred3_1 = len2;
        return getStringFromWasm0(ptr2, len2);
    } finally {
        wasm.__wbindgen_free(deferred3_0, deferred3_1, 1);
    }
}

export function init() {
    wasm.init();
}
export function __wbg_Error_67e7344beaa85059(arg0, arg1) {
    const ret = Error(getStringFromWasm0(arg0, arg1));
    return ret;
}
export function __wbg_Number_c54e7112a3fa7e3e(arg0) {
    const ret = Number(arg0);
    return ret;
}
export function __wbg_String_8564e559799eccda(arg0, arg1) {
    const ret = String(arg1);
    const ptr1 = passStringToWasm0(ret, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len1 = WASM_VECTOR_LEN;
    getDataViewMemory0().setInt32(arg0 + 4 * 1, len1, true);
    getDataViewMemory0().setInt32(arg0 + 4 * 0, ptr1, true);
}
export function __wbg_Window_241244be8c9776c1(arg0) {
    const ret = arg0.Window;
    return ret;
}
export function __wbg_WorkerGlobalScope_8623a7c9030fbce2(arg0) {
    const ret = arg0.WorkerGlobalScope;
    return ret;
}
export function __wbg___wbindgen_bigint_get_as_i64_b482365c149396c8(arg0, arg1) {
    const v = arg1;
    const ret = typeof(v) === 'bigint' ? v : undefined;
    getDataViewMemory0().setBigInt64(arg0 + 8 * 1, isLikeNone(ret) ? BigInt(0) : ret, true);
    getDataViewMemory0().setInt32(arg0 + 4 * 0, !isLikeNone(ret), true);
}
export function __wbg___wbindgen_boolean_get_7a12af2b3f899c5a(arg0) {
    const v = arg0;
    const ret = typeof(v) === 'boolean' ? v : undefined;
    return isLikeNone(ret) ? 0xFFFFFF : ret ? 1 : 0;
}
export function __wbg___wbindgen_debug_string_0e68cf47c9cbd9b0(arg0, arg1) {
    const ret = debugString(arg1);
    const ptr1 = passStringToWasm0(ret, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len1 = WASM_VECTOR_LEN;
    getDataViewMemory0().setInt32(arg0 + 4 * 1, len1, true);
    getDataViewMemory0().setInt32(arg0 + 4 * 0, ptr1, true);
}
export function __wbg___wbindgen_in_50072d4d6e45c193(arg0, arg1) {
    const ret = arg0 in arg1;
    return ret;
}
export function __wbg___wbindgen_is_bigint_60fc0336cb14f5d7(arg0) {
    const ret = typeof(arg0) === 'bigint';
    return ret;
}
export function __wbg___wbindgen_is_function_fcda5e3902d732fe(arg0) {
    const ret = typeof(arg0) === 'function';
    return ret;
}
export function __wbg___wbindgen_is_null_5160b3e381865372(arg0) {
    const ret = arg0 === null;
    return ret;
}
export function __wbg___wbindgen_is_object_edb6b15aa3afe12e(arg0) {
    const val = arg0;
    const ret = typeof(val) === 'object' && val !== null;
    return ret;
}
export function __wbg___wbindgen_is_string_c4f7cb494a2a21f1(arg0) {
    const ret = typeof(arg0) === 'string';
    return ret;
}
export function __wbg___wbindgen_is_undefined_8c687d0b90d5b524(arg0) {
    const ret = arg0 === undefined;
    return ret;
}
export function __wbg___wbindgen_jsval_eq_9fdcd3c0a860dd3b(arg0, arg1) {
    const ret = arg0 === arg1;
    return ret;
}
export function __wbg___wbindgen_jsval_loose_eq_3c30021c243b64cd(arg0, arg1) {
    const ret = arg0 == arg1;
    return ret;
}
export function __wbg___wbindgen_number_get_1dc732b810cb937c(arg0, arg1) {
    const obj = arg1;
    const ret = typeof(obj) === 'number' ? obj : undefined;
    getDataViewMemory0().setFloat64(arg0 + 8 * 1, isLikeNone(ret) ? 0 : ret, true);
    getDataViewMemory0().setInt32(arg0 + 4 * 0, !isLikeNone(ret), true);
}
export function __wbg___wbindgen_string_get_92ab86bb19cbc12f(arg0, arg1) {
    const obj = arg1;
    const ret = typeof(obj) === 'string' ? obj : undefined;
    var ptr1 = isLikeNone(ret) ? 0 : passStringToWasm0(ret, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    var len1 = WASM_VECTOR_LEN;
    getDataViewMemory0().setInt32(arg0 + 4 * 1, len1, true);
    getDataViewMemory0().setInt32(arg0 + 4 * 0, ptr1, true);
}
export function __wbg___wbindgen_throw_5d9e815e6fdf150f(arg0, arg1) {
    throw new Error(getStringFromWasm0(arg0, arg1));
}
export function __wbg__wbg_cb_unref_997e73d32238e655(arg0) {
    arg0._wbg_cb_unref();
}
export function __wbg_abort_03f8c5804715cb9a(arg0, arg1) {
    arg0.abort(arg1);
}
export function __wbg_abort_76dea11221098ae5(arg0) {
    arg0.abort();
}
export function __wbg_abort_f692a110ee403014() { return handleError(function (arg0) {
    arg0.abort();
}, arguments); }
export function __wbg_add_410a1a7101375ac7() { return handleError(function (arg0, arg1, arg2) {
    const ret = arg0.add(arg1, arg2);
    return ret;
}, arguments); }
export function __wbg_add_80525fb389c1e11e() { return handleError(function (arg0, arg1) {
    const ret = arg0.add(arg1);
    return ret;
}, arguments); }
export function __wbg_append_8e87c13f0f08f8cd() { return handleError(function (arg0, arg1, arg2, arg3, arg4) {
    arg0.append(getStringFromWasm0(arg1, arg2), getStringFromWasm0(arg3, arg4));
}, arguments); }
export function __wbg_arrayBuffer_06f3da76f071d37f() { return handleError(function (arg0) {
    const ret = arg0.arrayBuffer();
    return ret;
}, arguments); }
export function __wbg_bound_b651b81f2b3f6e0d() { return handleError(function (arg0, arg1, arg2, arg3) {
    const ret = IDBKeyRange.bound(arg0, arg1, arg2 !== 0, arg3 !== 0);
    return ret;
}, arguments); }
export function __wbg_btoa_2b7573b540f81582() { return handleError(function (arg0, arg1, arg2, arg3) {
    const ret = arg1.btoa(getStringFromWasm0(arg2, arg3));
    const ptr1 = passStringToWasm0(ret, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len1 = WASM_VECTOR_LEN;
    getDataViewMemory0().setInt32(arg0 + 4 * 1, len1, true);
    getDataViewMemory0().setInt32(arg0 + 4 * 0, ptr1, true);
}, arguments); }
export function __wbg_buffer_4a989bded7035f57(arg0) {
    const ret = arg0.buffer;
    return ret;
}
export function __wbg_byobRequest_f161fc37241dd3d4(arg0) {
    const ret = arg0.byobRequest;
    return isLikeNone(ret) ? 0 : addToExternrefTable0(ret);
}
export function __wbg_byteLength_0ddb1795e2c7e689(arg0) {
    const ret = arg0.byteLength;
    return ret;
}
export function __wbg_byteOffset_46eb015f52b6ad7d(arg0) {
    const ret = arg0.byteOffset;
    return ret;
}
export function __wbg_call_269c5566fbede3eb() { return handleError(function (arg0, arg1) {
    const ret = arg0.call(arg1);
    return ret;
}, arguments); }
export function __wbg_call_6bcf8d3e20937e46() { return handleError(function (arg0, arg1, arg2) {
    const ret = arg0.call(arg1, arg2);
    return ret;
}, arguments); }
export function __wbg_clearTimeout_1daad5c6ee4fcd3b(arg0) {
    const ret = clearTimeout(arg0);
    return ret;
}
export function __wbg_clearTimeout_3629d6209dfcc46e(arg0) {
    const ret = clearTimeout(arg0);
    return ret;
}
export function __wbg_clear_dec40bfb446d8509() { return handleError(function (arg0) {
    const ret = arg0.clear();
    return ret;
}, arguments); }
export function __wbg_close_22882088c136df25() { return handleError(function (arg0) {
    arg0.close();
}, arguments); }
export function __wbg_close_39a0adee471336e2(arg0) {
    arg0.close();
}
export function __wbg_close_8b609460dd26e367() { return handleError(function (arg0) {
    arg0.close();
}, arguments); }
export function __wbg_code_0589939084228780(arg0) {
    const ret = arg0.code;
    return ret;
}
export function __wbg_commit_ebd6d9676954e0d2() { return handleError(function (arg0) {
    arg0.commit();
}, arguments); }
export function __wbg_continue_b8815fef7aa0b75d() { return handleError(function (arg0) {
    arg0.continue();
}, arguments); }
export function __wbg_count_5d61922deb1f5d47() { return handleError(function (arg0) {
    const ret = arg0.count();
    return ret;
}, arguments); }
export function __wbg_count_8638a559df36f5af() { return handleError(function (arg0) {
    const ret = arg0.count();
    return ret;
}, arguments); }
export function __wbg_count_b7601a56f2fb1c82() { return handleError(function (arg0, arg1) {
    const ret = arg0.count(arg1);
    return ret;
}, arguments); }
export function __wbg_count_bd67c026dc7fcdfe() { return handleError(function (arg0, arg1) {
    const ret = arg0.count(arg1);
    return ret;
}, arguments); }
export function __wbg_createIndex_9f1f61554a67494f() { return handleError(function (arg0, arg1, arg2, arg3) {
    const ret = arg0.createIndex(getStringFromWasm0(arg1, arg2), arg3);
    return ret;
}, arguments); }
export function __wbg_createIndex_a953caa8cbe1f450() { return handleError(function (arg0, arg1, arg2, arg3, arg4) {
    const ret = arg0.createIndex(getStringFromWasm0(arg1, arg2), arg3, arg4);
    return ret;
}, arguments); }
export function __wbg_createObjectStore_615d9f80ee29af47() { return handleError(function (arg0, arg1, arg2) {
    const ret = arg0.createObjectStore(getStringFromWasm0(arg1, arg2));
    return ret;
}, arguments); }
export function __wbg_createObjectStore_c0a6e2a3e8976409() { return handleError(function (arg0, arg1, arg2, arg3) {
    const ret = arg0.createObjectStore(getStringFromWasm0(arg1, arg2), arg3);
    return ret;
}, arguments); }
export function __wbg_crypto_38df2bab126b63dc(arg0) {
    const ret = arg0.crypto;
    return ret;
}
export function __wbg_deleteDatabase_8d0a30e2f9578d09() { return handleError(function (arg0, arg1, arg2) {
    const ret = arg0.deleteDatabase(getStringFromWasm0(arg1, arg2));
    return ret;
}, arguments); }
export function __wbg_deleteObjectStore_b2ab76420e1c3a1f() { return handleError(function (arg0, arg1, arg2) {
    arg0.deleteObjectStore(getStringFromWasm0(arg1, arg2));
}, arguments); }
export function __wbg_delete_8f4a65ee3c10958c() { return handleError(function (arg0) {
    const ret = arg0.delete();
    return ret;
}, arguments); }
export function __wbg_delete_c66e14397c562c32() { return handleError(function (arg0, arg1) {
    const ret = arg0.delete(arg1);
    return ret;
}, arguments); }
export function __wbg_done_cffed884d87aa22e(arg0) {
    const ret = arg0.done;
    return ret;
}
export function __wbg_enqueue_6cb545d22db14f33() { return handleError(function (arg0, arg1) {
    arg0.enqueue(arg1);
}, arguments); }
export function __wbg_entries_972a87586902cf87(arg0) {
    const ret = Object.entries(arg0);
    return ret;
}
export function __wbg_entries_f9de04c09a57314e(arg0) {
    const ret = arg0.entries();
    return ret;
}
export function __wbg_error_64279b5bdc7324ac() { return handleError(function (arg0) {
    const ret = arg0.error;
    return isLikeNone(ret) ? 0 : addToExternrefTable0(ret);
}, arguments); }
export function __wbg_error_757e9472f8410341(arg0, arg1) {
    let deferred0_0;
    let deferred0_1;
    try {
        deferred0_0 = arg0;
        deferred0_1 = arg1;
        console.error(getStringFromWasm0(arg0, arg1));
    } finally {
        wasm.__wbindgen_free(deferred0_0, deferred0_1, 1);
    }
}
export function __wbg_fetch_7a37768ab67c5bd8(arg0) {
    const ret = fetch(arg0);
    return ret;
}
export function __wbg_fetch_920242d59bac2026(arg0, arg1) {
    const ret = arg0.fetch(arg1);
    return ret;
}
export function __wbg_from_a39669ce566077da(arg0) {
    const ret = Array.from(arg0);
    return ret;
}
export function __wbg_getAllKeys_6ab0d073434d8877() { return handleError(function (arg0, arg1) {
    const ret = arg0.getAllKeys(arg1);
    return ret;
}, arguments); }
export function __wbg_getAllKeys_7447eb3144e0ab86() { return handleError(function (arg0) {
    const ret = arg0.getAllKeys();
    return ret;
}, arguments); }
export function __wbg_getAllKeys_f4d9d1ce57225018() { return handleError(function (arg0, arg1) {
    const ret = arg0.getAllKeys(arg1);
    return ret;
}, arguments); }
export function __wbg_getAll_2437333369ac1277() { return handleError(function (arg0, arg1, arg2) {
    const ret = arg0.getAll(arg1, arg2 >>> 0);
    return ret;
}, arguments); }
export function __wbg_getAll_58414036eab7517d() { return handleError(function (arg0, arg1) {
    const ret = arg0.getAll(arg1);
    return ret;
}, arguments); }
export function __wbg_getAll_81779f44fd1012a2() { return handleError(function (arg0) {
    const ret = arg0.getAll();
    return ret;
}, arguments); }
export function __wbg_getAll_c1e152dd1bb9b630() { return handleError(function (arg0, arg1) {
    const ret = arg0.getAll(arg1);
    return ret;
}, arguments); }
export function __wbg_getAll_ce8eae0b0dc1a13d() { return handleError(function (arg0) {
    const ret = arg0.getAll();
    return ret;
}, arguments); }
export function __wbg_getItem_5d579ac5f70ace34() { return handleError(function (arg0, arg1, arg2, arg3) {
    const ret = arg1.getItem(getStringFromWasm0(arg2, arg3));
    var ptr1 = isLikeNone(ret) ? 0 : passStringToWasm0(ret, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    var len1 = WASM_VECTOR_LEN;
    getDataViewMemory0().setInt32(arg0 + 4 * 1, len1, true);
    getDataViewMemory0().setInt32(arg0 + 4 * 0, ptr1, true);
}, arguments); }
export function __wbg_getRandomValues_436a51d0629d84e1() { return handleError(function (arg0, arg1) {
    globalThis.crypto.getRandomValues(getArrayU8FromWasm0(arg0, arg1));
}, arguments); }
export function __wbg_getRandomValues_a678b7300e8ed57f() { return handleError(function (arg0, arg1) {
    globalThis.crypto.getRandomValues(getArrayU8FromWasm0(arg0, arg1));
}, arguments); }
export function __wbg_getRandomValues_c44a50d8cfdaebeb() { return handleError(function (arg0, arg1) {
    arg0.getRandomValues(arg1);
}, arguments); }
export function __wbg_getTime_65922ba0b59d55a7(arg0) {
    const ret = arg0.getTime();
    return ret;
}
export function __wbg_getTimezoneOffset_6e4850ad528ac37d(arg0) {
    const ret = arg0.getTimezoneOffset();
    return ret;
}
export function __wbg_get_0f0414f3a633bc89() { return handleError(function (arg0, arg1) {
    const ret = arg0.get(arg1);
    return ret;
}, arguments); }
export function __wbg_get_6cf5a4d4d8ad3c5a() { return handleError(function (arg0, arg1) {
    const ret = Reflect.get(arg0, arg1);
    return ret;
}, arguments); }
export function __wbg_get_989d0a1309644f2b() { return handleError(function (arg0, arg1) {
    const ret = Reflect.get(arg0, arg1);
    return ret;
}, arguments); }
export function __wbg_get_a1a386ee18563120() { return handleError(function (arg0, arg1) {
    const ret = arg0.get(arg1);
    return ret;
}, arguments); }
export function __wbg_get_b1f0ab13c737f856(arg0, arg1) {
    const ret = arg0[arg1 >>> 0];
    return ret;
}
export function __wbg_get_unchecked_363572bdd397d473(arg0, arg1) {
    const ret = arg0[arg1 >>> 0];
    return ret;
}
export function __wbg_get_with_ref_key_6412cf3094599694(arg0, arg1) {
    const ret = arg0[arg1];
    return ret;
}
export function __wbg_global_94a489d2e6a0c5fd(arg0) {
    const ret = arg0.global;
    return ret;
}
export function __wbg_has_464f8b9416279d65() { return handleError(function (arg0, arg1) {
    const ret = Reflect.has(arg0, arg1);
    return ret;
}, arguments); }
export function __wbg_headers_2594464ff62969f3(arg0) {
    const ret = arg0.headers;
    return ret;
}
export function __wbg_index_1194cffffe28f207() { return handleError(function (arg0, arg1, arg2) {
    const ret = arg0.index(getStringFromWasm0(arg1, arg2));
    return ret;
}, arguments); }
export function __wbg_indexedDB_2d959d90beb14c29() { return handleError(function (arg0) {
    const ret = arg0.indexedDB;
    return isLikeNone(ret) ? 0 : addToExternrefTable0(ret);
}, arguments); }
export function __wbg_indexedDB_47c354eb27472a00() { return handleError(function (arg0) {
    const ret = arg0.indexedDB;
    return isLikeNone(ret) ? 0 : addToExternrefTable0(ret);
}, arguments); }
export function __wbg_indexedDB_803e27668e681433() { return handleError(function (arg0) {
    const ret = arg0.indexedDB;
    return isLikeNone(ret) ? 0 : addToExternrefTable0(ret);
}, arguments); }
export function __wbg_instanceof_ArrayBuffer_d4ff01f8247925ae(arg0) {
    let result;
    try {
        result = arg0 instanceof ArrayBuffer;
    } catch (_) {
        result = false;
    }
    const ret = result;
    return ret;
}
export function __wbg_instanceof_CursorSys_383984afc1fa1bbc(arg0) {
    let result;
    try {
        result = arg0 instanceof IDBCursorWithValue;
    } catch (_) {
        result = false;
    }
    const ret = result;
    return ret;
}
export function __wbg_instanceof_DomException_ef94c1e709e3e062(arg0) {
    let result;
    try {
        result = arg0 instanceof DOMException;
    } catch (_) {
        result = false;
    }
    const ret = result;
    return ret;
}
export function __wbg_instanceof_Error_fe6fa771c78ee4cf(arg0) {
    let result;
    try {
        result = arg0 instanceof Error;
    } catch (_) {
        result = false;
    }
    const ret = result;
    return ret;
}
export function __wbg_instanceof_IdbCursor_29bd78c892b0cd1b(arg0) {
    let result;
    try {
        result = arg0 instanceof IDBCursor;
    } catch (_) {
        result = false;
    }
    const ret = result;
    return ret;
}
export function __wbg_instanceof_IdbDatabase_ae33ffa3aa7692d2(arg0) {
    let result;
    try {
        result = arg0 instanceof IDBDatabase;
    } catch (_) {
        result = false;
    }
    const ret = result;
    return ret;
}
export function __wbg_instanceof_IdbOpenDbRequest_02e2ab03818fe24d(arg0) {
    let result;
    try {
        result = arg0 instanceof IDBOpenDBRequest;
    } catch (_) {
        result = false;
    }
    const ret = result;
    return ret;
}
export function __wbg_instanceof_IdbRequest_a43e52936679d71f(arg0) {
    let result;
    try {
        result = arg0 instanceof IDBRequest;
    } catch (_) {
        result = false;
    }
    const ret = result;
    return ret;
}
export function __wbg_instanceof_Map_1ff6a2b54c899f0d(arg0) {
    let result;
    try {
        result = arg0 instanceof Map;
    } catch (_) {
        result = false;
    }
    const ret = result;
    return ret;
}
export function __wbg_instanceof_Response_6366a785e400039b(arg0) {
    let result;
    try {
        result = arg0 instanceof Response;
    } catch (_) {
        result = false;
    }
    const ret = result;
    return ret;
}
export function __wbg_instanceof_Uint8Array_598adc0fef426aa8(arg0) {
    let result;
    try {
        result = arg0 instanceof Uint8Array;
    } catch (_) {
        result = false;
    }
    const ret = result;
    return ret;
}
export function __wbg_instanceof_Window_a3b8566f0a9c5d1a(arg0) {
    let result;
    try {
        result = arg0 instanceof Window;
    } catch (_) {
        result = false;
    }
    const ret = result;
    return ret;
}
export function __wbg_isArray_5674713bb7b79043(arg0) {
    const ret = Array.isArray(arg0);
    return ret;
}
export function __wbg_isArray_ce01375355c1ee4b(arg0) {
    const ret = Array.isArray(arg0);
    return ret;
}
export function __wbg_isSafeInteger_8f51c743827d1ec5(arg0) {
    const ret = Number.isSafeInteger(arg0);
    return ret;
}
export function __wbg_item_6897d8c7b5343cb5(arg0, arg1, arg2) {
    const ret = arg1.item(arg2 >>> 0);
    var ptr1 = isLikeNone(ret) ? 0 : passStringToWasm0(ret, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    var len1 = WASM_VECTOR_LEN;
    getDataViewMemory0().setInt32(arg0 + 4 * 1, len1, true);
    getDataViewMemory0().setInt32(arg0 + 4 * 0, ptr1, true);
}
export function __wbg_iterator_22ddeb808cf55a6f() {
    const ret = Symbol.iterator;
    return ret;
}
export function __wbg_key_cbf07e72de8fe203() { return handleError(function (arg0) {
    const ret = arg0.key;
    return ret;
}, arguments); }
export function __wbg_length_31bdaf014f5fbde2(arg0) {
    const ret = arg0.length;
    return ret;
}
export function __wbg_length_4e1adc0d42e23620(arg0) {
    const ret = arg0.length;
    return ret;
}
export function __wbg_localStorage_72d815b9c2adaaad() { return handleError(function (arg0) {
    const ret = arg0.localStorage;
    return isLikeNone(ret) ? 0 : addToExternrefTable0(ret);
}, arguments); }
export function __wbg_log_1f8cbb01c83d06c2(arg0, arg1, arg2, arg3, arg4, arg5, arg6, arg7) {
    let deferred0_0;
    let deferred0_1;
    try {
        deferred0_0 = arg0;
        deferred0_1 = arg1;
        console.log(getStringFromWasm0(arg0, arg1), getStringFromWasm0(arg2, arg3), getStringFromWasm0(arg4, arg5), getStringFromWasm0(arg6, arg7));
    } finally {
        wasm.__wbindgen_free(deferred0_0, deferred0_1, 1);
    }
}
export function __wbg_log_a54ca6b45e09078a(arg0, arg1) {
    let deferred0_0;
    let deferred0_1;
    try {
        deferred0_0 = arg0;
        deferred0_1 = arg1;
        console.log(getStringFromWasm0(arg0, arg1));
    } finally {
        wasm.__wbindgen_free(deferred0_0, deferred0_1, 1);
    }
}
export function __wbg_lowerBound_9c881ff10d3043b1() { return handleError(function (arg0, arg1) {
    const ret = IDBKeyRange.lowerBound(arg0, arg1 !== 0);
    return ret;
}, arguments); }
export function __wbg_mark_6b7f03786f5e4d61(arg0, arg1) {
    performance.mark(getStringFromWasm0(arg0, arg1));
}
export function __wbg_measure_0e21b33a1c6e3a29() { return handleError(function (arg0, arg1, arg2, arg3) {
    let deferred0_0;
    let deferred0_1;
    let deferred1_0;
    let deferred1_1;
    try {
        deferred0_0 = arg0;
        deferred0_1 = arg1;
        deferred1_0 = arg2;
        deferred1_1 = arg3;
        performance.measure(getStringFromWasm0(arg0, arg1), getStringFromWasm0(arg2, arg3));
    } finally {
        wasm.__wbindgen_free(deferred0_0, deferred0_1, 1);
        wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
    }
}, arguments); }
export function __wbg_message_1cbc5bc03dcf1dee(arg0) {
    const ret = arg0.message;
    return ret;
}
export function __wbg_message_d6ab3d8a5aedce1c(arg0, arg1) {
    const ret = arg1.message;
    const ptr1 = passStringToWasm0(ret, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len1 = WASM_VECTOR_LEN;
    getDataViewMemory0().setInt32(arg0 + 4 * 1, len1, true);
    getDataViewMemory0().setInt32(arg0 + 4 * 0, ptr1, true);
}
export function __wbg_msCrypto_bd5a034af96bcba6(arg0) {
    const ret = arg0.msCrypto;
    return ret;
}
export function __wbg_name_7c7adff93f0cf4a3(arg0, arg1) {
    const ret = arg1.name;
    const ptr1 = passStringToWasm0(ret, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len1 = WASM_VECTOR_LEN;
    getDataViewMemory0().setInt32(arg0 + 4 * 1, len1, true);
    getDataViewMemory0().setInt32(arg0 + 4 * 0, ptr1, true);
}
export function __wbg_name_da8a16520b339861(arg0, arg1) {
    const ret = arg1.name;
    const ptr1 = passStringToWasm0(ret, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len1 = WASM_VECTOR_LEN;
    getDataViewMemory0().setInt32(arg0 + 4 * 1, len1, true);
    getDataViewMemory0().setInt32(arg0 + 4 * 0, ptr1, true);
}
export function __wbg_new_0_35540e542ba689d2() {
    const ret = new Date();
    return ret;
}
export function __wbg_new_0afe64b4dc16ab74() { return handleError(function () {
    const ret = new Headers();
    return ret;
}, arguments); }
export function __wbg_new_180f1022bb6ee517(arg0) {
    const ret = new Date(arg0);
    return ret;
}
export function __wbg_new_1da3429bc3c4541c(arg0) {
    const ret = new Uint8Array(arg0);
    return ret;
}
export function __wbg_new_227d7c05414eb861() {
    const ret = new Error();
    return ret;
}
export function __wbg_new_8d36e20aa758e411() {
    const ret = new Map();
    return ret;
}
export function __wbg_new_a32a1ab6c6655abe(arg0, arg1) {
    const ret = new Error(getStringFromWasm0(arg0, arg1));
    return ret;
}
export function __wbg_new_baa0a0207935dd43() { return handleError(function () {
    const ret = new AbortController();
    return ret;
}, arguments); }
export function __wbg_new_bebc3f4757acf305() {
    const ret = new Object();
    return ret;
}
export function __wbg_new_ffa92086ea89f79c() {
    const ret = new Array();
    return ret;
}
export function __wbg_new_from_slice_4ee02165f9de919e(arg0, arg1) {
    const ret = new Uint8Array(getArrayU8FromWasm0(arg0, arg1));
    return ret;
}
export function __wbg_new_typed_6f8b0d724fe26c07(arg0, arg1) {
    try {
        var state0 = {a: arg0, b: arg1};
        var cb0 = (arg0, arg1) => {
            const a = state0.a;
            state0.a = 0;
            try {
                return wasm_bindgen_44795cbd6e887d93___convert__closures_____invoke___js_sys_2a1fd27e2a0a91c2___Function_fn_wasm_bindgen_44795cbd6e887d93___JsValue_____wasm_bindgen_44795cbd6e887d93___sys__Undefined___js_sys_2a1fd27e2a0a91c2___Function_fn_wasm_bindgen_44795cbd6e887d93___JsValue_____wasm_bindgen_44795cbd6e887d93___sys__Undefined_______true_(a, state0.b, arg0, arg1);
            } finally {
                state0.a = a;
            }
        };
        const ret = new Promise(cb0);
        return ret;
    } finally {
        state0.a = 0;
    }
}
export function __wbg_new_with_byte_offset_and_length_492c969e8b5da8a4(arg0, arg1, arg2) {
    const ret = new Uint8Array(arg0, arg1 >>> 0, arg2 >>> 0);
    return ret;
}
export function __wbg_new_with_length_5ffeddb9d9fbb96f(arg0) {
    const ret = new Uint8Array(arg0 >>> 0);
    return ret;
}
export function __wbg_new_with_message_and_name_7667d9c3995c96c6() { return handleError(function (arg0, arg1, arg2, arg3) {
    const ret = new DOMException(getStringFromWasm0(arg0, arg1), getStringFromWasm0(arg2, arg3));
    return ret;
}, arguments); }
export function __wbg_new_with_str_and_init_2f31a77deda127fd() { return handleError(function (arg0, arg1, arg2) {
    const ret = new Request(getStringFromWasm0(arg0, arg1), arg2);
    return ret;
}, arguments); }
export function __wbg_next_95053e306b1c3aed(arg0) {
    const ret = arg0.next;
    return ret;
}
export function __wbg_next_f31ecb8646d2c605() { return handleError(function (arg0) {
    const ret = arg0.next();
    return ret;
}, arguments); }
export function __wbg_node_84ea875411254db1(arg0) {
    const ret = arg0.node;
    return ret;
}
export function __wbg_now_d1fb6650485d7f3e() {
    const ret = Date.now();
    return ret;
}
export function __wbg_now_e7c6795a7f81e10f(arg0) {
    const ret = arg0.now();
    return ret;
}
export function __wbg_objectStoreNames_0d5b2223ea497c57(arg0) {
    const ret = arg0.objectStoreNames;
    return ret;
}
export function __wbg_objectStore_54d81e3f83434edc() { return handleError(function (arg0, arg1, arg2) {
    const ret = arg0.objectStore(getStringFromWasm0(arg1, arg2));
    return ret;
}, arguments); }
export function __wbg_oldVersion_bb3dbfcb7fa0f143(arg0) {
    const ret = arg0.oldVersion;
    return ret;
}
export function __wbg_openCursor_0dd2dcacbef4f54c() { return handleError(function (arg0) {
    const ret = arg0.openCursor();
    return ret;
}, arguments); }
export function __wbg_openCursor_5da5b1efa49865f1() { return handleError(function (arg0) {
    const ret = arg0.openCursor();
    return ret;
}, arguments); }
export function __wbg_openCursor_7c6d6688eed62e99() { return handleError(function (arg0, arg1) {
    const ret = arg0.openCursor(arg1);
    return ret;
}, arguments); }
export function __wbg_openCursor_dc039c9f0b9436eb() { return handleError(function (arg0, arg1) {
    const ret = arg0.openCursor(arg1);
    return ret;
}, arguments); }
export function __wbg_openKeyCursor_19b63ee4e121f9e1() { return handleError(function (arg0, arg1, arg2) {
    const ret = arg0.openKeyCursor(arg1, __wbindgen_enum_IdbCursorDirection[arg2]);
    return ret;
}, arguments); }
export function __wbg_openKeyCursor_8917f442a8f9b479() { return handleError(function (arg0, arg1, arg2) {
    const ret = arg0.openKeyCursor(arg1, __wbindgen_enum_IdbCursorDirection[arg2]);
    return ret;
}, arguments); }
export function __wbg_openKeyCursor_ce87a21d497149cd() { return handleError(function (arg0, arg1) {
    const ret = arg0.openKeyCursor(arg1);
    return ret;
}, arguments); }
export function __wbg_open_56eb9bc2b4228b89() { return handleError(function (arg0, arg1, arg2, arg3) {
    const ret = arg0.open(getStringFromWasm0(arg1, arg2), arg3 >>> 0);
    return ret;
}, arguments); }
export function __wbg_open_b30e07208deb4158() { return handleError(function (arg0, arg1, arg2, arg3) {
    const ret = arg0.open(getStringFromWasm0(arg1, arg2), arg3);
    return ret;
}, arguments); }
export function __wbg_open_bdaea92fb1357c10() { return handleError(function (arg0, arg1, arg2) {
    const ret = arg0.open(getStringFromWasm0(arg1, arg2));
    return ret;
}, arguments); }
export function __wbg_parse_6937a9050adfb0e1() { return handleError(function (arg0, arg1) {
    const ret = JSON.parse(getStringFromWasm0(arg0, arg1));
    return ret;
}, arguments); }
export function __wbg_performance_3fcf6e32a7e1ed0a(arg0) {
    const ret = arg0.performance;
    return ret;
}
export function __wbg_process_44c7a14e11e9f69e(arg0) {
    const ret = arg0.process;
    return ret;
}
export function __wbg_prototypesetcall_ae9f5e7459250748(arg0, arg1, arg2) {
    Uint8Array.prototype.set.call(getArrayU8FromWasm0(arg0, arg1), arg2);
}
export function __wbg_push_bfdf956ba476f65b(arg0, arg1) {
    const ret = arg0.push(arg1);
    return ret;
}
export function __wbg_put_7d52c69d9da5ab22() { return handleError(function (arg0, arg1, arg2) {
    const ret = arg0.put(arg1, arg2);
    return ret;
}, arguments); }
export function __wbg_put_eec520d1fad9114f() { return handleError(function (arg0, arg1) {
    const ret = arg0.put(arg1);
    return ret;
}, arguments); }
export function __wbg_queueMicrotask_85c90f6987555d65(arg0) {
    const ret = arg0.queueMicrotask;
    return ret;
}
export function __wbg_queueMicrotask_f6a1fa10b81d1fc0(arg0) {
    queueMicrotask(arg0);
}
export function __wbg_randomFillSync_6c25eac9869eb53c() { return handleError(function (arg0, arg1) {
    arg0.randomFillSync(arg1);
}, arguments); }
export function __wbg_readyState_4f74e29bb400e81e(arg0) {
    const ret = arg0.readyState;
    return (__wbindgen_enum_IdbRequestReadyState.indexOf(ret) + 1 || 3) - 1;
}
export function __wbg_removeItem_350dd4387dc52350() { return handleError(function (arg0, arg1, arg2) {
    arg0.removeItem(getStringFromWasm0(arg1, arg2));
}, arguments); }
export function __wbg_request_64abeba15a72c084(arg0) {
    const ret = arg0.request;
    return ret;
}
export function __wbg_request_72a78988f2edecad(arg0) {
    const ret = arg0.request;
    return ret;
}
export function __wbg_require_b4edbdcf3e2a1ef0() { return handleError(function () {
    const ret = module.require;
    return ret;
}, arguments); }
export function __wbg_resolve_35ec7e0c6af4c82c(arg0) {
    const ret = Promise.resolve(arg0);
    return ret;
}
export function __wbg_respond_83a71686e927ca32() { return handleError(function (arg0, arg1) {
    arg0.respond(arg1 >>> 0);
}, arguments); }
export function __wbg_result_1ee542654b519ece() { return handleError(function (arg0) {
    const ret = arg0.result;
    return ret;
}, arguments); }
export function __wbg_setItem_51f02f76b24c5d18() { return handleError(function (arg0, arg1, arg2, arg3, arg4) {
    arg0.setItem(getStringFromWasm0(arg1, arg2), getStringFromWasm0(arg3, arg4));
}, arguments); }
export function __wbg_setTimeout_56bcdccbad22fd44() { return handleError(function (arg0, arg1) {
    const ret = setTimeout(arg0, arg1);
    return ret;
}, arguments); }
export function __wbg_setTimeout_b4a9096784635514(arg0, arg1) {
    const ret = setTimeout(arg0, arg1);
    return ret;
}
export function __wbg_set_13d25b81ab403f5e(arg0, arg1, arg2) {
    arg0[arg1 >>> 0] = arg2;
}
export function __wbg_set_5f2ad37e5e02dc7b(arg0, arg1, arg2) {
    arg0.set(getArrayU8FromWasm0(arg1, arg2));
}
export function __wbg_set_6be42768c690e380(arg0, arg1, arg2) {
    arg0[arg1] = arg2;
}
export function __wbg_set_bf6dde4923b9b059(arg0, arg1, arg2) {
    const ret = arg0.set(arg1, arg2);
    return ret;
}
export function __wbg_set_body_f39cee72c74a5b02(arg0, arg1) {
    arg0.body = arg1;
}
export function __wbg_set_cache_6e8c35a5ed76edb1(arg0, arg1) {
    arg0.cache = __wbindgen_enum_RequestCache[arg1];
}
export function __wbg_set_credentials_bf1d3e014213a571(arg0, arg1) {
    arg0.credentials = __wbindgen_enum_RequestCredentials[arg1];
}
export function __wbg_set_headers_31d373f68f28bf76(arg0, arg1) {
    arg0.headers = arg1;
}
export function __wbg_set_key_path_e2e7db6b955ed9ee(arg0, arg1) {
    arg0.keyPath = arg1;
}
export function __wbg_set_method_82e6c3c083da0734(arg0, arg1, arg2) {
    arg0.method = getStringFromWasm0(arg1, arg2);
}
export function __wbg_set_mode_f7e56ed768d0ade0(arg0, arg1) {
    arg0.mode = __wbindgen_enum_RequestMode[arg1];
}
export function __wbg_set_onabort_1d0052c8998c83c3(arg0, arg1) {
    arg0.onabort = arg1;
}
export function __wbg_set_oncomplete_18b4816241642ed1(arg0, arg1) {
    arg0.oncomplete = arg1;
}
export function __wbg_set_onerror_1938848fb2ee8cc8(arg0, arg1) {
    arg0.onerror = arg1;
}
export function __wbg_set_onerror_eddf5d542c5b47d7(arg0, arg1) {
    arg0.onerror = arg1;
}
export function __wbg_set_onsuccess_4e8073573d1f2b4e(arg0, arg1) {
    arg0.onsuccess = arg1;
}
export function __wbg_set_onupgradeneeded_49d6dd635034b91f(arg0, arg1) {
    arg0.onupgradeneeded = arg1;
}
export function __wbg_set_referrer_13cf56bbccbb2ceb(arg0, arg1, arg2) {
    arg0.referrer = getStringFromWasm0(arg1, arg2);
}
export function __wbg_set_referrer_policy_dac1caa6e937479a(arg0, arg1) {
    arg0.referrerPolicy = __wbindgen_enum_ReferrerPolicy[arg1];
}
export function __wbg_set_signal_f4b32f40f19786f4(arg0, arg1) {
    arg0.signal = arg1;
}
export function __wbg_set_unique_2cc42f748668f41d(arg0, arg1) {
    arg0.unique = arg1 !== 0;
}
export function __wbg_signal_45367c6c2255877a(arg0) {
    const ret = arg0.signal;
    return ret;
}
export function __wbg_stack_3b0d974bbf31e44f(arg0, arg1) {
    const ret = arg1.stack;
    const ptr1 = passStringToWasm0(ret, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len1 = WASM_VECTOR_LEN;
    getDataViewMemory0().setInt32(arg0 + 4 * 1, len1, true);
    getDataViewMemory0().setInt32(arg0 + 4 * 0, ptr1, true);
}
export function __wbg_static_accessor_GLOBAL_8eb4cd83130a11a0() {
    const ret = typeof global === 'undefined' ? null : global;
    return isLikeNone(ret) ? 0 : addToExternrefTable0(ret);
}
export function __wbg_static_accessor_GLOBAL_THIS_1e7044f654e934db() {
    const ret = typeof globalThis === 'undefined' ? null : globalThis;
    return isLikeNone(ret) ? 0 : addToExternrefTable0(ret);
}
export function __wbg_static_accessor_SELF_d8b50611246a6d92() {
    const ret = typeof self === 'undefined' ? null : self;
    return isLikeNone(ret) ? 0 : addToExternrefTable0(ret);
}
export function __wbg_static_accessor_WINDOW_fd0bc376bf0f8b42() {
    const ret = typeof window === 'undefined' ? null : window;
    return isLikeNone(ret) ? 0 : addToExternrefTable0(ret);
}
export function __wbg_status_88ccc72fe7bea621(arg0) {
    const ret = arg0.status;
    return ret;
}
export function __wbg_stringify_54b3d9b61602aee6() { return handleError(function (arg0) {
    const ret = JSON.stringify(arg0);
    return ret;
}, arguments); }
export function __wbg_subarray_1daff70dde20c145(arg0, arg1, arg2) {
    const ret = arg0.subarray(arg1 >>> 0, arg2 >>> 0);
    return ret;
}
export function __wbg_target_3e80dc66a5936e87(arg0) {
    const ret = arg0.target;
    return isLikeNone(ret) ? 0 : addToExternrefTable0(ret);
}
export function __wbg_then_7a850dae4493f353(arg0, arg1, arg2) {
    const ret = arg0.then(arg1, arg2);
    return ret;
}
export function __wbg_then_b830475380919203(arg0, arg1) {
    const ret = arg0.then(arg1);
    return ret;
}
export function __wbg_toString_794a30e85c994f2a(arg0) {
    const ret = arg0.toString();
    return ret;
}
export function __wbg_transaction_3019f53fe122f6c8() { return handleError(function (arg0, arg1, arg2) {
    const ret = arg0.transaction(arg1, __wbindgen_enum_IdbTransactionMode[arg2]);
    return ret;
}, arguments); }
export function __wbg_transaction_7cc4506d6cff2b51(arg0) {
    const ret = arg0.transaction;
    return isLikeNone(ret) ? 0 : addToExternrefTable0(ret);
}
export function __wbg_transaction_83426eb500ee2f91(arg0) {
    const ret = arg0.transaction;
    return ret;
}
export function __wbg_transaction_a42a399637cea7ea() { return handleError(function (arg0, arg1, arg2, arg3) {
    const ret = arg0.transaction(getStringFromWasm0(arg1, arg2), __wbindgen_enum_IdbTransactionMode[arg3]);
    return ret;
}, arguments); }
export function __wbg_update_400214dbaa3d90bb() { return handleError(function (arg0, arg1) {
    const ret = arg0.update(arg1);
    return ret;
}, arguments); }
export function __wbg_upperBound_33e5eb97e0acea4d() { return handleError(function (arg0, arg1) {
    const ret = IDBKeyRange.upperBound(arg0, arg1 !== 0);
    return ret;
}, arguments); }
export function __wbg_url_f1948b0d7f881e02(arg0, arg1) {
    const ret = arg1.url;
    const ptr1 = passStringToWasm0(ret, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len1 = WASM_VECTOR_LEN;
    getDataViewMemory0().setInt32(arg0 + 4 * 1, len1, true);
    getDataViewMemory0().setInt32(arg0 + 4 * 0, ptr1, true);
}
export function __wbg_value_481b5a1bd4c573bd() { return handleError(function (arg0) {
    const ret = arg0.value;
    return ret;
}, arguments); }
export function __wbg_value_c227f843d21da141(arg0) {
    const ret = arg0.value;
    return ret;
}
export function __wbg_version_b9d21acf19fbf402(arg0) {
    const ret = arg0.version;
    return ret;
}
export function __wbg_versions_276b2795b1c6a219(arg0) {
    const ret = arg0.versions;
    return ret;
}
export function __wbg_view_d8c7b26e4d4650f1(arg0) {
    const ret = arg0.view;
    return isLikeNone(ret) ? 0 : addToExternrefTable0(ret);
}
export function __wbg_warn_d3544c7814fab534(arg0) {
    console.warn(arg0);
}
export function __wbg_wasmclient_new(arg0) {
    const ret = WasmClient.__wrap(arg0);
    return ret;
}
export function __wbindgen_generic_0000000000000001(arg0, arg1) {
    // Cast intrinsic for `Closure(Closure { owned: true, function: Function { arguments: [Externref], shim_idx: 16101, ret: Result(Unit), inner_ret: Some(Result(Unit)) }, mutable: true }) -> Externref`.
    const ret = makeMutClosure(arg0, arg1, wasm_bindgen_44795cbd6e887d93___convert__closures_____invoke___wasm_bindgen_44795cbd6e887d93___JsValue__core_f0fd674eaa06beef___result__Result_____wasm_bindgen_44795cbd6e887d93___JsError___true_);
    return ret;
}
export function __wbindgen_generic_0000000000000002(arg0, arg1) {
    // Cast intrinsic for `Closure(Closure { owned: true, function: Function { arguments: [NamedExternref("Event")], shim_idx: 11418, ret: Unit, inner_ret: Some(Unit) }, mutable: true }) -> Externref`.
    const ret = makeMutClosure(arg0, arg1, wasm_bindgen_44795cbd6e887d93___convert__closures_____invoke___web_sys_b02f4f08ec488aae___features__gen_Event__Event______true_);
    return ret;
}
export function __wbindgen_generic_0000000000000003(arg0, arg1) {
    // Cast intrinsic for `Closure(Closure { owned: true, function: Function { arguments: [NamedExternref("IDBVersionChangeEvent")], shim_idx: 10790, ret: Result(Unit), inner_ret: Some(Result(Unit)) }, mutable: true }) -> Externref`.
    const ret = makeMutClosure(arg0, arg1, wasm_bindgen_44795cbd6e887d93___convert__closures_____invoke___web_sys_b02f4f08ec488aae___features__gen_IdbVersionChangeEvent__IdbVersionChangeEvent__core_f0fd674eaa06beef___result__Result_____wasm_bindgen_44795cbd6e887d93___JsValue___true_);
    return ret;
}
export function __wbindgen_generic_0000000000000004(arg0, arg1) {
    // Cast intrinsic for `Closure(Closure { owned: true, function: Function { arguments: [], shim_idx: 11420, ret: Unit, inner_ret: Some(Unit) }, mutable: true }) -> Externref`.
    const ret = makeMutClosure(arg0, arg1, wasm_bindgen_44795cbd6e887d93___convert__closures_____invoke_______true_);
    return ret;
}
export function __wbindgen_generic_0000000000000005(arg0, arg1) {
    // Cast intrinsic for `Closure(Closure { owned: true, function: Function { arguments: [], shim_idx: 11511, ret: Unit, inner_ret: Some(Unit) }, mutable: true }) -> Externref`.
    const ret = makeMutClosure(arg0, arg1, wasm_bindgen_44795cbd6e887d93___convert__closures_____invoke_______true__1_);
    return ret;
}
export function __wbindgen_generic_0000000000000006(arg0, arg1) {
    // Cast intrinsic for `Closure(Closure { owned: true, function: Function { arguments: [], shim_idx: 14884, ret: Unit, inner_ret: Some(Unit) }, mutable: true }) -> Externref`.
    const ret = makeMutClosure(arg0, arg1, wasm_bindgen_44795cbd6e887d93___convert__closures_____invoke_______true__2_);
    return ret;
}
export function __wbindgen_generic_0000000000000007(arg0) {
    // Cast intrinsic for `F64 -> Externref`.
    const ret = arg0;
    return ret;
}
export function __wbindgen_generic_0000000000000008(arg0) {
    // Cast intrinsic for `I64 -> Externref`.
    const ret = arg0;
    return ret;
}
export function __wbindgen_generic_0000000000000009(arg0, arg1) {
    // Cast intrinsic for `Ref(Slice(U8)) -> NamedExternref("Uint8Array")`.
    const ret = getArrayU8FromWasm0(arg0, arg1);
    return ret;
}
export function __wbindgen_generic_000000000000000a(arg0, arg1) {
    // Cast intrinsic for `Ref(String) -> Externref`.
    const ret = getStringFromWasm0(arg0, arg1);
    return ret;
}
export function __wbindgen_generic_000000000000000b(arg0) {
    // Cast intrinsic for `U64 -> Externref`.
    const ret = BigInt.asUintN(64, arg0);
    return ret;
}
export function __wbindgen_init_externref_table() {
    const table = wasm.__wbindgen_externrefs;
    const offset = table.grow(4);
    table.set(0, undefined);
    table.set(offset + 0, undefined);
    table.set(offset + 1, null);
    table.set(offset + 2, true);
    table.set(offset + 3, false);
}
function wasm_bindgen_44795cbd6e887d93___convert__closures_____invoke_______true_(arg0, arg1) {
    wasm.wasm_bindgen_44795cbd6e887d93___convert__closures_____invoke_______true_(arg0, arg1);
}

function wasm_bindgen_44795cbd6e887d93___convert__closures_____invoke_______true__1_(arg0, arg1) {
    wasm.wasm_bindgen_44795cbd6e887d93___convert__closures_____invoke_______true__1_(arg0, arg1);
}

function wasm_bindgen_44795cbd6e887d93___convert__closures_____invoke_______true__2_(arg0, arg1) {
    wasm.wasm_bindgen_44795cbd6e887d93___convert__closures_____invoke_______true__2_(arg0, arg1);
}

function wasm_bindgen_44795cbd6e887d93___convert__closures_____invoke___web_sys_b02f4f08ec488aae___features__gen_Event__Event______true_(arg0, arg1, arg2) {
    wasm.wasm_bindgen_44795cbd6e887d93___convert__closures_____invoke___web_sys_b02f4f08ec488aae___features__gen_Event__Event______true_(arg0, arg1, arg2);
}

function wasm_bindgen_44795cbd6e887d93___convert__closures_____invoke___wasm_bindgen_44795cbd6e887d93___JsValue__core_f0fd674eaa06beef___result__Result_____wasm_bindgen_44795cbd6e887d93___JsError___true_(arg0, arg1, arg2) {
    const ret = wasm.wasm_bindgen_44795cbd6e887d93___convert__closures_____invoke___wasm_bindgen_44795cbd6e887d93___JsValue__core_f0fd674eaa06beef___result__Result_____wasm_bindgen_44795cbd6e887d93___JsError___true_(arg0, arg1, arg2);
    if (ret[1]) {
        throw takeFromExternrefTable0(ret[0]);
    }
}

function wasm_bindgen_44795cbd6e887d93___convert__closures_____invoke___web_sys_b02f4f08ec488aae___features__gen_IdbVersionChangeEvent__IdbVersionChangeEvent__core_f0fd674eaa06beef___result__Result_____wasm_bindgen_44795cbd6e887d93___JsValue___true_(arg0, arg1, arg2) {
    const ret = wasm.wasm_bindgen_44795cbd6e887d93___convert__closures_____invoke___web_sys_b02f4f08ec488aae___features__gen_IdbVersionChangeEvent__IdbVersionChangeEvent__core_f0fd674eaa06beef___result__Result_____wasm_bindgen_44795cbd6e887d93___JsValue___true_(arg0, arg1, arg2);
    if (ret[1]) {
        throw takeFromExternrefTable0(ret[0]);
    }
}

function wasm_bindgen_44795cbd6e887d93___convert__closures_____invoke___js_sys_2a1fd27e2a0a91c2___Function_fn_wasm_bindgen_44795cbd6e887d93___JsValue_____wasm_bindgen_44795cbd6e887d93___sys__Undefined___js_sys_2a1fd27e2a0a91c2___Function_fn_wasm_bindgen_44795cbd6e887d93___JsValue_____wasm_bindgen_44795cbd6e887d93___sys__Undefined_______true_(arg0, arg1, arg2, arg3) {
    wasm.wasm_bindgen_44795cbd6e887d93___convert__closures_____invoke___js_sys_2a1fd27e2a0a91c2___Function_fn_wasm_bindgen_44795cbd6e887d93___JsValue_____wasm_bindgen_44795cbd6e887d93___sys__Undefined___js_sys_2a1fd27e2a0a91c2___Function_fn_wasm_bindgen_44795cbd6e887d93___JsValue_____wasm_bindgen_44795cbd6e887d93___sys__Undefined_______true_(arg0, arg1, arg2, arg3);
}


const __wbindgen_enum_IdbCursorDirection = ["next", "nextunique", "prev", "prevunique"];


const __wbindgen_enum_IdbRequestReadyState = ["pending", "done"];


const __wbindgen_enum_IdbTransactionMode = ["readonly", "readwrite", "versionchange", "readwriteflush", "cleanup"];


const __wbindgen_enum_ReadableStreamType = ["bytes"];


const __wbindgen_enum_ReferrerPolicy = ["", "no-referrer", "no-referrer-when-downgrade", "origin", "origin-when-cross-origin", "unsafe-url", "same-origin", "strict-origin", "strict-origin-when-cross-origin"];


const __wbindgen_enum_RequestCache = ["default", "no-store", "reload", "no-cache", "force-cache", "only-if-cached"];


const __wbindgen_enum_RequestCredentials = ["omit", "same-origin", "include"];


const __wbindgen_enum_RequestMode = ["same-origin", "no-cors", "cors", "navigate"];
const IntoUnderlyingByteSourceFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_intounderlyingbytesource_free(ptr, 1));
const IntoUnderlyingSinkFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_intounderlyingsink_free(ptr, 1));
const IntoUnderlyingSourceFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_intounderlyingsource_free(ptr, 1));
const WasmClientFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_wasmclient_free(ptr, 1));

function addToExternrefTable0(obj) {
    const idx = wasm.__externref_table_alloc();
    wasm.__wbindgen_externrefs.set(idx, obj);
    return idx;
}

const CLOSURE_DTORS = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(state => wasm.__wbindgen_destroy_closure(state.a, state.b));

function debugString(val) {
    // primitive types
    const type = typeof val;
    if (type == 'number' || type == 'boolean' || val == null) {
        return  `${val}`;
    }
    if (type == 'string') {
        return `"${val}"`;
    }
    if (type == 'symbol') {
        const description = val.description;
        if (description == null) {
            return 'Symbol';
        } else {
            return `Symbol(${description})`;
        }
    }
    if (type == 'function') {
        const name = val.name;
        if (typeof name == 'string' && name.length > 0) {
            return `Function(${name})`;
        } else {
            return 'Function';
        }
    }
    // objects
    if (Array.isArray(val)) {
        const length = val.length;
        let debug = '[';
        if (length > 0) {
            debug += debugString(val[0]);
        }
        for(let i = 1; i < length; i++) {
            debug += ', ' + debugString(val[i]);
        }
        debug += ']';
        return debug;
    }
    // Test for built-in
    const builtInMatches = /\[object ([^\]]+)\]/.exec(toString.call(val));
    let className;
    if (builtInMatches && builtInMatches.length > 1) {
        className = builtInMatches[1];
    } else {
        // Failed to match the standard '[object ClassName]'
        return toString.call(val);
    }
    if (className == 'Object') {
        // we're a user defined class or Object
        // JSON.stringify avoids problems with cycles, and is generally much
        // easier than looping through ownProperties of `val`.
        try {
            return 'Object(' + JSON.stringify(val) + ')';
        } catch (_) {
            return 'Object';
        }
    }
    // errors
    if (val instanceof Error) {
        return `${val.name}: ${val.message}\n${val.stack}`;
    }
    // TODO we could test for more things here, like `Set`s and `Map`s.
    return className;
}

function getArrayU8FromWasm0(ptr, len) {
    ptr = ptr >>> 0;
    return getUint8ArrayMemory0().subarray(ptr / 1, ptr / 1 + len);
}

let cachedDataViewMemory0 = null;
function getDataViewMemory0() {
    if (cachedDataViewMemory0 === null || cachedDataViewMemory0.buffer.detached === true || (cachedDataViewMemory0.buffer.detached === undefined && cachedDataViewMemory0.buffer !== wasm.memory.buffer)) {
        cachedDataViewMemory0 = new DataView(wasm.memory.buffer);
    }
    return cachedDataViewMemory0;
}

let cachedFloat32ArrayMemory0 = null;
function getFloat32ArrayMemory0() {
    if (cachedFloat32ArrayMemory0 === null || cachedFloat32ArrayMemory0.byteLength === 0) {
        cachedFloat32ArrayMemory0 = new Float32Array(wasm.memory.buffer);
    }
    return cachedFloat32ArrayMemory0;
}

function getStringFromWasm0(ptr, len) {
    return decodeText(ptr >>> 0, len);
}

let cachedUint8ArrayMemory0 = null;
function getUint8ArrayMemory0() {
    if (cachedUint8ArrayMemory0 === null || cachedUint8ArrayMemory0.byteLength === 0) {
        cachedUint8ArrayMemory0 = new Uint8Array(wasm.memory.buffer);
    }
    return cachedUint8ArrayMemory0;
}

function handleError(f, args) {
    try {
        return f.apply(this, args);
    } catch (e) {
        const idx = addToExternrefTable0(e);
        wasm.__wbindgen_exn_store(idx);
    }
}

function isLikeNone(x) {
    return x === undefined || x === null;
}

function makeMutClosure(arg0, arg1, f) {
    const state = { a: arg0, b: arg1, cnt: 1 };
    const real = (...args) => {

        // First up with a closure we increment the internal reference
        // count. This ensures that the Rust closure environment won't
        // be deallocated while we're invoking it.
        state.cnt++;
        const a = state.a;
        state.a = 0;
        try {
            return f(a, state.b, ...args);
        } finally {
            state.a = a;
            real._wbg_cb_unref();
        }
    };
    real._wbg_cb_unref = () => {
        if (--state.cnt === 0) {
            wasm.__wbindgen_destroy_closure(state.a, state.b);
            state.a = 0;
            CLOSURE_DTORS.unregister(state);
        }
    };
    CLOSURE_DTORS.register(real, state, state);
    return real;
}

function passArray8ToWasm0(arg, malloc) {
    const ptr = malloc(arg.length * 1, 1) >>> 0;
    getUint8ArrayMemory0().set(arg, ptr / 1);
    WASM_VECTOR_LEN = arg.length;
    return ptr;
}

function passArrayF32ToWasm0(arg, malloc) {
    const ptr = malloc(arg.length * 4, 4) >>> 0;
    getFloat32ArrayMemory0().set(arg, ptr / 4);
    WASM_VECTOR_LEN = arg.length;
    return ptr;
}

function passArrayJsValueToWasm0(array, malloc) {
    const ptr = malloc(array.length * 4, 4) >>> 0;
    for (let i = 0; i < array.length; i++) {
        const add = addToExternrefTable0(array[i]);
        getDataViewMemory0().setUint32(ptr + 4 * i, add, true);
    }
    WASM_VECTOR_LEN = array.length;
    return ptr;
}

function passStringToWasm0(arg, malloc, realloc) {
    if (realloc === undefined) {
        const buf = cachedTextEncoder.encode(arg);
        const ptr = malloc(buf.length, 1) >>> 0;
        getUint8ArrayMemory0().subarray(ptr, ptr + buf.length).set(buf);
        WASM_VECTOR_LEN = buf.length;
        return ptr;
    }

    let len = arg.length;
    let ptr = malloc(len, 1) >>> 0;

    const mem = getUint8ArrayMemory0();

    let offset = 0;

    for (; offset < len; offset++) {
        const code = arg.charCodeAt(offset);
        if (code > 0x7F) break;
        mem[ptr + offset] = code;
    }
    if (offset !== len) {
        if (offset !== 0) {
            arg = arg.slice(offset);
        }
        ptr = realloc(ptr, len, len = offset + arg.length * 3, 1) >>> 0;
        const view = getUint8ArrayMemory0().subarray(ptr + offset, ptr + len);
        const ret = cachedTextEncoder.encodeInto(arg, view);

        offset += ret.written;
        ptr = realloc(ptr, len, offset, 1) >>> 0;
    }

    WASM_VECTOR_LEN = offset;
    return ptr;
}

function takeFromExternrefTable0(idx) {
    const value = wasm.__wbindgen_externrefs.get(idx);
    wasm.__externref_table_dealloc(idx);
    return value;
}

let cachedTextDecoder = new TextDecoder('utf-8', { ignoreBOM: true, fatal: true });
cachedTextDecoder.decode();
const MAX_SAFARI_DECODE_BYTES = 2146435072;
let numBytesDecoded = 0;
function decodeText(ptr, len) {
    numBytesDecoded += len;
    if (numBytesDecoded >= MAX_SAFARI_DECODE_BYTES) {
        cachedTextDecoder = new TextDecoder('utf-8', { ignoreBOM: true, fatal: true });
        cachedTextDecoder.decode();
        numBytesDecoded = len;
    }
    return cachedTextDecoder.decode(getUint8ArrayMemory0().subarray(ptr, ptr + len));
}

const cachedTextEncoder = new TextEncoder();

if (!('encodeInto' in cachedTextEncoder)) {
    cachedTextEncoder.encodeInto = function (arg, view) {
        const buf = cachedTextEncoder.encode(arg);
        view.set(buf);
        return {
            read: arg.length,
            written: buf.length
        };
    };
}

let WASM_VECTOR_LEN = 0;


let wasm;
export function __wbg_set_wasm(val) {
    wasm = val;
}
